import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { BookOpen, Brain, Mic, MicOff, Plus, TrendingUp, Target, LogOut, Trash2, CheckCircle2, Circle, Sparkles, RefreshCw, Download, Lightbulb, Search, Filter, BarChart3, FileText, Edit, X, Save, Trash, Crown, Lock } from 'lucide-react';

import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import SentimentTrendChart from '@/components/SentimentTrendChart';
import EmotionalTrendsChart from '@/components/EmotionalTrendsChart';
import ActionPlanStatsChart from '@/components/ActionPlanStatsChart';
import SubscriptionBanner from '@/components/SubscriptionBanner';
import UpgradeModal from '@/components/UpgradeModal';
import ConsentModal from '@/components/ConsentModal';
import DailyQuote from '@/components/DailyQuote';
import InstallPrompt from '@/components/InstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reflection prompts for daily guidance
const REFLECTION_PROMPTS = [
  "What happened? Why did it happen that way? What can I learn? What will I do differently next time?",
  "What went well today? What challenged me? What am I grateful for?",
  "What did I accomplish? What obstacles did I face? How did I overcome them?",
  "What emotion am I feeling right now? What triggered it? What can I learn from it?",
  "What was my biggest win today? What would I do differently? What's my focus for tomorrow?",
  "What surprised me today? How did I respond? What does this teach me about myself?",
  "What patterns am I noticing? What behaviors do I want to change? What's one small step I can take?",
  "What feedback did I receive? How did it make me feel? What truth is in it?",
  "What drained my energy today? What energized me? How can I have more of the latter?",
  "What assumption did I make? Was it accurate? What would I do with a fresh perspective?"
];

const Dashboard = () => {
  // Initialise user from localStorage synchronously so the first render already
  // knows whether to draw the full or lite layout. Avoids the flash of the
  // full-app tab bar for users who arrived via /lite/<code>.
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [reflections, setReflections] = useState([]);
  const [insights, setInsights] = useState([]);
  const [weeklyInsights, setWeeklyInsights] = useState([]);
  const [monthlyInsights, setMonthlyInsights] = useState([]);
  const [quarterlyInsights, setQuarterlyInsights] = useState([]);
  const [yearlyInsights, setYearlyInsights] = useState([]);
  const [actionPlans, setActionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New reflection form
  const [newReflection, setNewReflection] = useState('');
  const [tags, setTags] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const recognitionRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const VOICE_MAX_SECONDS = 60;
  
  // Edit reflection
  const [editingReflection, setEditingReflection] = useState(null);
  const [editText, setEditText] = useState('');
  const [editTags, setEditTags] = useState('');
  
  // Prompts and tag suggestions
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate input state
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedSentiment, setSelectedSentiment] = useState('');
  const [allTags, setAllTags] = useState([]);
  
  // Sentiment trend
  const [sentimentTrend, setSentimentTrend] = useState([]);
  
  // Edit action plan
  const [editingActionPlan, setEditingActionPlan] = useState(null);
  const [editPlanData, setEditPlanData] = useState({ title: '', description: '', tasks: [] });
  
  // Action plan filtering and sorting
  const [actionPlanSort, setActionPlanSort] = useState('newest');
  const [actionPlanStats, setActionPlanStats] = useState(null);
  
  // Create action plan dialog
  const [showActionPlanDialog, setShowActionPlanDialog] = useState(false);
  const [newActionPlan, setNewActionPlan] = useState({ title: '', description: '', tasks: '' });
  const [showCreateActionPlanFromReview, setShowCreateActionPlanFromReview] = useState(false);
  const [reviewActionItems, setReviewActionItems] = useState([]);
  const [reviewPeriodType, setReviewPeriodType] = useState('');
  
  // Generating insights
  const [generatingInsights, setGeneratingInsights] = useState(false);
  // Track which weekly insight cards are expanded. Defaults: newest expanded,
  // older ones collapsed. The map only stores user overrides — fallback uses
  // index === 0.
  const [weeklyExpanded, setWeeklyExpanded] = useState({});
  
  // Reviews (new system)
  const [reviews, setReviews] = useState([]);
  const [weeklyReviews, setWeeklyReviews] = useState([]);
  const [monthlyReviews, setMonthlyReviews] = useState([]);
  const [generatingReview, setGeneratingReview] = useState(false);
  const [reviewTemplate, setReviewTemplate] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  
  // Subscription
  const [subscription, setSubscription] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [site, setSite] = useState(null);
  const [reviewContent, setReviewContent] = useState('');
  const [reviewAnswers, setReviewAnswers] = useState({});
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewContent, setEditReviewContent] = useState('');
  const [emotionalTrends, setEmotionalTrends] = useState([]);
  const [insightsSubTab, setInsightsSubTab] = useState('ai');
  const [canCreateWeeklyReview, setCanCreateWeeklyReview] = useState(true);
  const [canCreateMonthlyReview, setCanCreateMonthlyReview] = useState(true);
  const [canGenerateWeeklyInsights, setCanGenerateWeeklyInsights] = useState(true);
  const [canGenerateMonthlyInsights, setCanGenerateMonthlyInsights] = useState(true);
  const [canGenerateQuarterlyInsights, setCanGenerateQuarterlyInsights] = useState(true);
  const [canGenerateYearlyInsights, setCanGenerateYearlyInsights] = useState(true);
  
  // User guide
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showReflectivePractitionerGuide, setShowReflectivePractitionerGuide] = useState(false);

  useEffect(() => {
    fetchData();
    checkReviewStatus();
    fetchSubscriptionStatus();
    fetchPricing();
    fetchSiteConfig();
  }, []);

  const fetchSiteConfig = async () => {
    try {
      const response = await axios.get(`${API}/site/config`);
      setSite(response.data);
    } catch (error) {
      console.error('Failed to fetch site config:', error);
    }
  };
  
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/subscription/config`);
      setPricing(response.data.pricing);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  // Check insights status when insights change
  useEffect(() => {
    if (insights.length >= 0) {
      checkInsightsStatus();
    }
  }, [insights]);

  // Separate effect for filter changes
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [searchQuery, selectedTag, selectedSentiment]);

  useEffect(() => {
    if (!loading) {
      fetchActionPlans().then(plans => setActionPlans(plans));
      fetchActionPlanStats();
    }
  }, [actionPlanSort]);

  const fetchActionPlans = async (status = null) => {
    try {
      const params = { sort: actionPlanSort };
      if (status) params.status = status;
      
      const res = await axios.get(`${API}/action-plans`, { params, withCredentials: true });
      return res.data;
    } catch (error) {
      console.error('Error fetching action plans:', error);
      return [];
    }
  };

  const fetchActionPlanStats = async () => {
    try {
      const res = await axios.get(`${API}/action-plans/stats`, { withCredentials: true });
      setActionPlanStats(res.data);
    } catch (error) {
      console.error('Error fetching action plan stats:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Build query params
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedTag) params.tag = selectedTag;
      if (selectedSentiment) params.sentiment = selectedSentiment;
      
      console.log('Fetching reflections with params:', params);
      
      const [reflectionsRes, insightsRes, plansRes, trendRes, reviewsRes, emotionalTrendsRes] = await Promise.all([
        axios.get(`${API}/reflections`, { params, withCredentials: true }),
        axios.get(`${API}/insights`, { withCredentials: true }),
        fetchActionPlans(),
        axios.get(`${API}/reflections/sentiment-trend?days=30`, { withCredentials: true }),
        axios.get(`${API}/reviews`, { withCredentials: true }),
        axios.get(`${API}/analytics/emotional-trends?period_type=weekly`, { withCredentials: true })
      ]);
      
      console.log('Received reflections:', reflectionsRes.data.length);
      
      setReflections(reflectionsRes.data);
      setInsights(insightsRes.data);
      setActionPlans(plansRes);
      setSentimentTrend(trendRes.data.trend || []);
      
      // Separate insights by period
      const allInsights = insightsRes.data;
      setWeeklyInsights(allInsights.filter(i => i.period === 'weekly'));
      setMonthlyInsights(allInsights.filter(i => i.period === 'monthly'));
      setQuarterlyInsights(allInsights.filter(i => i.period === 'quarterly'));
      setYearlyInsights(allInsights.filter(i => i.period === 'yearly'));
      
      // Separate reviews by type
      const allReviews = reviewsRes.data;
      setReviews(allReviews);
      setWeeklyReviews(allReviews.filter(r => r.period_type === 'weekly'));
      setMonthlyReviews(allReviews.filter(r => r.period_type === 'monthly'));
      
      setEmotionalTrends(emotionalTrendsRes.data || []);
      
      // Extract all unique tags
      const tags = new Set();
      reflectionsRes.data.forEach(r => r.tags.forEach(tag => tags.add(tag)));
      setAllTags(Array.from(tags).sort());
      
      // Fetch action plan stats
      await fetchActionPlanStats();
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Lite users belong to an org pilot — send them back to /lite/<code>
    // (their actual entry point) rather than the public marketing landing.
    const liteReturn = user?.app_mode === 'lite' && user?.org_code
      ? `/lite/${encodeURIComponent(user.org_code)}`
      : '/';
    localStorage.removeItem('user');
    document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    window.location.href = liteReturn;
  };

  const startRecording = () => {
    // Check if browser supports Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      toast.error('Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let finalTranscript = '';

      recognition.onstart = () => {
        setIsRecording(true);
        setVoiceTranscript('');
        toast.success(`Listening... Speak now (max ${VOICE_MAX_SECONDS}s)`);
        // Hard cap recording so we don't run transcription on long rambles
        if (recordingTimerRef.current) clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch (_) { /* recognition already stopped */ }
            toast.info(`Voice note auto-stopped at ${VOICE_MAX_SECONDS}s`);
          }
        }, VOICE_MAX_SECONDS * 1000);
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Show the combined transcript
        const fullTranscript = finalTranscript + interimTranscript;
        setVoiceTranscript(fullTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        toast.error(`Error: ${event.error}`);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
        if (recordingTimerRef.current) {
          clearTimeout(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        // Get the final transcript from state
        if (finalTranscript.trim()) {
          // Add voice transcript to reflection text
          if (newReflection.trim()) {
            setNewReflection(newReflection + '\n\n[Voice Note]: ' + finalTranscript.trim());
          } else {
            setNewReflection(finalTranscript.trim());
          }
          setVoiceTranscript('');
          toast.success('Voice transcribed and added to reflection!');
        } else {
          toast.info('No speech detected');
          setVoiceTranscript('');
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      toast.error('Could not start voice recording');
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
    }
  };

  const handleCreateReflection = async () => {
    if (!newReflection.trim()) {
      toast.error('Please write something or use voice recording');
      return;
    }

    try {
      const tagsList = tags.split(',').map(t => t.trim()).filter(t => t);
      
      await axios.post(
        `${API}/reflections`,
        {
          text: newReflection,
          tags: tagsList
        },
        { withCredentials: true }
      );

      toast.success('Reflection saved successfully!');
      setNewReflection('');
      setTags('');
      setVoiceTranscript('');
      fetchData();
    } catch (error) {
      console.error('Error creating reflection:', error);
      toast.error(error.response?.data?.detail || 'Failed to save reflection');
    }
  };

  const handleGenerateInsights = async (period = 'weekly') => {
    // Check feature access for premium insights
    if (period !== 'weekly' && subscription && !subscription.features?.[`${period}_insights`]) {
      setShowUpgradeModal(true);
      toast.error(`${period.charAt(0).toUpperCase() + period.slice(1)} insights require Premium. Upgrade to access this feature!`);
      return;
    }
    
    setGeneratingInsights(true);
    try {
      await axios.post(
        `${API}/insights/generate?period=${period}`,
        {},
        { withCredentials: true }
      );
      toast.success('Insights generated with action plan suggestion!');
      fetchData();
      checkInsightsStatus();
    } catch (error) {
      console.error('Error generating insights:', error);
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      }
      toast.error(error.response?.data?.detail || 'Failed to generate insights');
    } finally {
      setGeneratingInsights(false);
    }
  };

  const handleDeleteInsight = async (insightId) => {
    // Find the insight to check its period
    const insight = insights.find(i => i.id === insightId);
    let confirmMessage = 'Are you sure you want to delete this insight? This action cannot be undone.';
    
    if (insight && insight.period === 'monthly') {
      confirmMessage = '⚠️ Warning: Monthly insights are needed to generate quarterly insights.\n\nAre you sure you want to delete this insight?';
    } else if (insight && insight.period === 'quarterly') {
      confirmMessage = '⚠️ Warning: Quarterly insights are needed to generate yearly insights.\n\nAre you sure you want to delete this insight?';
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(`${API}/insights/${insightId}`, { withCredentials: true });
      toast.success('Insight deleted successfully!');
      fetchData();
      checkInsightsStatus();
    } catch (error) {
      console.error('Error deleting insight:', error);
      toast.error('Failed to delete insight');
    }
  };

  const handleCreateActionPlanFromInsight = async (insightId) => {
    // Check feature access for action plans
    if (subscription && !subscription.features?.action_plans) {
      setShowUpgradeModal(true);
      toast.error('Action Plans require Premium. Upgrade to access this feature!');
      return;
    }
    
    try {
      await axios.post(
        `${API}/insights/${insightId}/create-action-plan`,
        {},
        { withCredentials: true }
      );
      toast.success('Action plan created! Check the Action Plans tab.');
      fetchData();
    } catch (error) {
      console.error('Error creating action plan:', error);
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      }
      toast.error(error.response?.data?.detail || 'Failed to create action plan');
    }
  };

  const handleCreateActionPlan = async () => {
    // Check feature access for action plans
    if (subscription && !subscription.features?.action_plans) {
      setShowUpgradeModal(true);
      toast.error('Action Plans require Premium. Upgrade to access this feature!');
      setShowActionPlanDialog(false);
      return;
    }
    
    if (!newActionPlan.title || !newActionPlan.description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const tasksList = newActionPlan.tasks.split('\n').map(t => t.trim()).filter(t => t);
      
      await axios.post(
        `${API}/action-plans`,
        {
          title: newActionPlan.title,
          description: newActionPlan.description,
          tasks: tasksList
        },
        { withCredentials: true }
      );

      toast.success('Action plan created!');
      setNewActionPlan({ title: '', description: '', tasks: '' });
      setShowActionPlanDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error creating action plan:', error);
      toast.error('Failed to create action plan');
    }
  };

  const handleToggleTask = async (planId, tasks) => {
    try {
      await axios.patch(
        `${API}/action-plans/${planId}`,
        { tasks },
        { withCredentials: true }
      );
      
      // Check if all tasks are now completed
      const allCompleted = tasks.every(task => task.completed);
      
      if (allCompleted) {
        // Automatically close the plan
        await axios.put(
          `${API}/action-plans/${planId}/status?status=closed`,
          {},
          { withCredentials: true }
        );
        toast.success('🎉 All tasks completed! Plan moved to Closed Plans.');
      }
      
      fetchData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleReopenPlan = async (planId) => {
    try {
      await axios.put(
        `${API}/action-plans/${planId}/status?status=active`,
        {},
        { withCredentials: true }
      );
      toast.success('Action plan reopened!');
      fetchData();
    } catch (error) {
      console.error('Error reopening plan:', error);
      toast.error('Failed to reopen plan');
    }
  };

  const handleDeleteReflection = async (id) => {
    try {
      // Get the reflection before deleting to check if it's a weekly review
      const reflectionToDelete = reflections.find(r => r.id === id);
      await axios.delete(`${API}/reflections/${id}`, { withCredentials: true });
      toast.success('Reflection deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting reflection:', error);
      toast.error('Failed to delete reflection');
    }
  };

  const handleEditReflection = (reflection) => {
    setEditingReflection(reflection.id);
    setEditText(reflection.text);
    setEditTags(reflection.tags.join(', '));
  };

  const handleCancelEdit = () => {
    setEditingReflection(null);
    setEditText('');
    setEditTags('');
  };

  const handleSaveEdit = async (id) => {
    try {
      const tagsList = editTags.split(',').map(t => t.trim()).filter(t => t);
      
      await axios.patch(
        `${API}/reflections/${id}`,
        {
          text: editText,
          tags: tagsList
        },
        { withCredentials: true }
      );

      toast.success('Reflection updated');
      setEditingReflection(null);
      setEditText('');
      setEditTags('');
      fetchData();
    } catch (error) {
      console.error('Error updating reflection:', error);
      toast.error('Failed to update reflection');
    }
  };

  const handleDeleteActionPlan = async (id) => {
    try {
      await axios.delete(`${API}/action-plans/${id}`, { withCredentials: true });
      toast.success('Action plan deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting action plan:', error);
      toast.error('Failed to delete action plan');
    }
  };

  const handleEditActionPlan = (plan) => {
    setEditingActionPlan(plan.id);
    setEditPlanData({
      title: plan.title,
      description: plan.description,
      tasks: plan.tasks
    });
  };

  const handleSaveActionPlan = async () => {
    if (!editPlanData.title || !editPlanData.description) {
      toast.error('Please fill in title and description');
      return;
    }

    try {
      await axios.put(
        `${API}/action-plans/${editingActionPlan}`,
        editPlanData,
        { withCredentials: true }
      );
      toast.success('Action plan updated!');
      setEditingActionPlan(null);
      fetchData();
    } catch (error) {
      console.error('Error updating action plan:', error);
      toast.error('Failed to update action plan');
    }
  };

  const handleCancelEditActionPlan = () => {
    setEditingActionPlan(null);
    setEditPlanData({ title: '', description: '', tasks: [] });
  };

  // Check review and insights status
  const checkReviewStatus = async () => {
    try {
      const [weeklyReviewStatus, monthlyReviewStatus] = await Promise.all([
        axios.get(`${API}/reviews/check-status/weekly`, { withCredentials: true }),
        axios.get(`${API}/reviews/check-status/monthly`, { withCredentials: true })
      ]);
      
      setCanCreateWeeklyReview(weeklyReviewStatus.data.can_create);
      setCanCreateMonthlyReview(monthlyReviewStatus.data.can_create);
    } catch (error) {
      console.error('Error checking review status:', error);
    }
  };

  const checkInsightsStatus = async () => {
    try {
      // Check if insights exist for current period
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay() + 1); // Monday
      weekStart.setHours(0, 0, 0, 0);
      
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Calculate quarter start
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const quarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      
      // Year start
      const yearStart = new Date(now.getFullYear(), 0, 1);
      
      // Check existing insights
      const hasWeeklyInsight = insights.some(insight => {
        const insightDate = new Date(insight.created_at);
        return insight.period === 'weekly' && insightDate >= weekStart;
      });
      
      const hasMonthlyInsight = insights.some(insight => {
        const insightDate = new Date(insight.created_at);
        return insight.period === 'monthly' && insightDate >= monthStart;
      });
      
      const hasQuarterlyInsight = insights.some(insight => {
        const insightDate = new Date(insight.created_at);
        return insight.period === 'quarterly' && insightDate >= quarterStart;
      });
      
      const hasYearlyInsight = insights.some(insight => {
        const insightDate = new Date(insight.created_at);
        return insight.period === 'yearly' && insightDate >= yearStart;
      });
      
      setCanGenerateWeeklyInsights(!hasWeeklyInsight);
      setCanGenerateMonthlyInsights(!hasMonthlyInsight);
      setCanGenerateQuarterlyInsights(!hasQuarterlyInsight);
      setCanGenerateYearlyInsights(!hasYearlyInsight);
    } catch (error) {
      console.error('Error checking insights status:', error);
    }
  };

  // Review handlers
  const handleGenerateReviewTemplate = async (periodType) => {
    setGeneratingReview(true);
    try {
      const response = await axios.post(
        `${API}/reviews/generate-template/${periodType}`,
        {},
        { withCredentials: true }
      );
      setReviewTemplate({ ...response.data, period_type: periodType });
      setReviewContent(response.data.template);
      
      // Initialize review answers for Q&A format
      if (periodType === 'weekly') {
        setReviewAnswers({
          wins: '',
          challenges: '',
          patterns: '',
          gratitude: '',
          top3Actions: ''
        });
      } else {
        setReviewAnswers({
          achievements: '',
          obstacles: '',
          habits: '',
          proud: '',
          lessons: '',
          top3Actions: ''
        });
      }
      
      setShowReviewDialog(true);
      toast.success(`${periodType.charAt(0).toUpperCase() + periodType.slice(1)} review template generated!`);
    } catch (error) {
      console.error('Error generating review template:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate review template');
    } finally {
      setGeneratingReview(false);
    }
  };

  const handleSaveReview = async () => {
    // Check if at least one answer is provided
    const hasAnswers = Object.values(reviewAnswers).some(answer => answer && answer.trim());
    if (!hasAnswers) {
      toast.error('Please answer at least one question');
      return;
    }

    try {
      // Format content based on period type
      let formattedContent;
      const stats = reviewTemplate.stats || {};
      
      if (reviewTemplate.period_type === 'weekly') {
        formattedContent = `# Weekly Review - ${reviewTemplate.period_label || new Date().toLocaleDateString()}

## This Week's Summary
- Total reflections: ${stats.total || 0}
- Positive moments: ${stats.sentiments?.positive || 0}
- Neutral moments: ${stats.sentiments?.neutral || 0}
- Challenging moments: ${stats.sentiments?.negative || 0}
${stats.top_tags?.length > 0 ? `- Key themes: ${stats.top_tags.join(', ')}` : ''}

## 1. What were my biggest wins this week?
${reviewAnswers.wins || '_No response_'}

## 2. What challenges did I face and how did I handle them?
${reviewAnswers.challenges || '_No response_'}

## 3. What patterns am I noticing in my thoughts and behaviors?
${reviewAnswers.patterns || '_No response_'}

## 4. What am I grateful for this week?
${reviewAnswers.gratitude || '_No response_'}

## 5. What are your key action items for next week?
${reviewAnswers.top3Actions || '_No response_'}
`;
      } else {
        formattedContent = `# Monthly Review - ${reviewTemplate.period_label || new Date().toLocaleDateString()}

## This Month's Summary
- Total reflections: ${stats.total || 0}
- Positive moments: ${stats.sentiments?.positive || 0}
- Neutral moments: ${stats.sentiments?.neutral || 0}
- Challenging moments: ${stats.sentiments?.negative || 0}
${stats.top_tags?.length > 0 ? `- Key themes: ${stats.top_tags.join(', ')}` : ''}

## 1. What were my biggest achievements this month?
${reviewAnswers.achievements || '_No response_'}

## 2. What obstacles did I overcome?
${reviewAnswers.obstacles || '_No response_'}

## 3. What habits or patterns emerged?
${reviewAnswers.habits || '_No response_'}

## 4. What am I most proud of this month?
${reviewAnswers.proud || '_No response_'}

## 5. What lessons did I learn?
${reviewAnswers.lessons || '_No response_'}

## 6. What are your key action items for next month?
${reviewAnswers.top3Actions || '_No response_'}
`;
      }

      await axios.post(
        `${API}/reviews`,
        {
          period_type: reviewTemplate.period_type,
          content: formattedContent,
          stats: stats
        },
        { withCredentials: true }
      );
      
      // Check for action items BEFORE clearing state or showing success
      const actionItemsText = reviewAnswers.top3Actions;
      const periodType = reviewTemplate.period_type;
      
      // Extract action items (lines with content)
      let lines = [];
      if (actionItemsText && actionItemsText.trim().length > 0) {
        lines = actionItemsText.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => line.replace(/^[\-\*\•\d]+\.?\s*/, '').trim())
          .filter(line => line.length > 0)
          .slice(0, 20); // Limit to maximum 20 action items
      }
      
      toast.success('Review saved!');
      
      // Close review dialog
      setShowReviewDialog(false);
      setReviewTemplate(null);
      setReviewContent('');
      setReviewAnswers({});
      
      // Show action plan dialog if there are actions
      if (lines.length > 0) {
        setReviewActionItems(lines);
        setReviewPeriodType(periodType);
        setTimeout(() => {
          setShowCreateActionPlanFromReview(true);
        }, 300);
      }
      
      fetchData();
      checkReviewStatus();
    } catch (error) {
      console.error('Error saving review:', error);
      toast.error(error.response?.data?.detail || 'Failed to save review');
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review.id);
    setEditReviewContent(review.content);
  };

  const handleSaveEditReview = async () => {
    try {
      await axios.put(
        `${API}/reviews/${editingReview}`,
        { content: editReviewContent },
        { withCredentials: true }
      );
      toast.success('Review updated!');
      setEditingReview(null);
      fetchData();
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Failed to update review');
    }
  };

  const handleCancelEditReview = () => {
    setEditingReview(null);
    setEditReviewContent('');
  };

  const handleDeleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { withCredentials: true });
      toast.success('Review deleted');
      fetchData();
      checkReviewStatus();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const handleExportReview = async (reviewId, format) => {
    try {
      const response = await axios.get(`${API}/reviews/${reviewId}/export?format=${format}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `review_${reviewId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Review exported as ${format.toUpperCase()}!`);
    } catch (error) {
      console.error('Error exporting review:', error);
      toast.error('Failed to export review');
    }
  };

  const handleCreateActionPlanFromReview = async () => {
    try {
      const periodLabel = reviewPeriodType === 'weekly' ? 'Week' : 'Month';
      
      // Backend expects tasks as array of strings
      const tasks = reviewActionItems;

      await axios.post(
        `${API}/action-plans`,
        {
          title: `Action Plan from ${periodLabel}ly Review`,
          description: `Top actions identified during ${periodLabel.toLowerCase()}ly review`,
          tasks: tasks
        },
        { withCredentials: true }
      );

      toast.success('Action plan created!');
      setShowCreateActionPlanFromReview(false);
      setReviewActionItems([]);
      setReviewPeriodType('');
      fetchData();
    } catch (error) {
      console.error('Error creating action plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to create action plan');
    }
  };

  const handleToggleEmotionalTrendsPeriod = async (periodType) => {
    try {
      const response = await axios.get(`${API}/analytics/emotional-trends?period_type=${periodType}`, {
        withCredentials: true
      });
      setEmotionalTrends(response.data || []);
    } catch (error) {
      console.error('Error fetching emotional trends:', error);
      toast.error('Failed to load emotional trends');
    }
  };

  const handleExportInsight = async (insight) => {
    try {
      // Use jsPDF for client-side PDF generation
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();
      
      let y = 20;
      const lineHeight = 7;
      const margin = 20;
      const maxWidth = 170;
      const pageHeight = 280;
      
      // Helper function to check and add new page
      const checkPageBreak = (neededSpace = lineHeight) => {
        if (y + neededSpace > pageHeight) {
          doc.addPage();
          y = 20;
          return true;
        }
        return false;
      };
      
      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(`${insight.period.toUpperCase()} INSIGHTS`, margin, y);
      y += lineHeight * 2;
      
      // Date
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date(insight.created_at).toLocaleDateString()}`, margin, y);
      y += lineHeight * 2;
      doc.setTextColor(0);
      
      // Key Themes
      checkPageBreak(lineHeight * (insight.themes.length + 2));
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Key Themes:', margin, y);
      y += lineHeight;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      insight.themes.forEach(theme => {
        const themeLines = doc.splitTextToSize(`• ${theme}`, maxWidth - 5);
        themeLines.forEach(line => {
          checkPageBreak();
          doc.text(line, margin + 5, y);
          y += lineHeight;
        });
      });
      y += lineHeight;
      
      // Summary
      checkPageBreak(lineHeight * 3);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Summary:', margin, y);
      y += lineHeight;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const summaryLines = doc.splitTextToSize(insight.summary, maxWidth);
      summaryLines.forEach(line => {
        checkPageBreak();
        doc.text(line, margin, y);
        y += lineHeight;
      });
      y += lineHeight;
      
      // Emotional Trend
      if (Object.keys(insight.sentiment_trend).length > 0) {
        checkPageBreak(lineHeight * (Object.keys(insight.sentiment_trend).length + 2));
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Emotional Trend:', margin, y);
        y += lineHeight;
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        Object.entries(insight.sentiment_trend).forEach(([sentiment, count]) => {
          checkPageBreak();
          doc.text(`${sentiment}: ${count}`, margin + 5, y);
          y += lineHeight;
        });
        y += lineHeight;
      }
      
      // Suggested Action Plan
      if (insight.suggested_action_plan) {
        checkPageBreak(lineHeight * 5);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Suggested Action Plan:', margin, y);
        y += lineHeight;
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        const titleLines = doc.splitTextToSize(insight.suggested_action_plan.title, maxWidth);
        titleLines.forEach(line => {
          checkPageBreak();
          doc.text(line, margin, y);
          y += lineHeight;
        });
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        const descLines = doc.splitTextToSize(insight.suggested_action_plan.description, maxWidth);
        descLines.forEach(line => {
          checkPageBreak();
          doc.text(line, margin, y);
          y += lineHeight;
        });
        y += lineHeight;
        
        checkPageBreak(lineHeight * 2);
        doc.setFont(undefined, 'bold');
        doc.text('Tasks:', margin, y);
        y += lineHeight;
        doc.setFont(undefined, 'normal');
        insight.suggested_action_plan.tasks.forEach(task => {
          const taskLines = doc.splitTextToSize(`• ${task}`, maxWidth - 5);
          taskLines.forEach(line => {
            checkPageBreak();
            doc.text(line, margin + 5, y);
            y += lineHeight;
          });
        });
      }
      
      // Save PDF
      doc.save(`${insight.period}_insights_${new Date(insight.created_at).toISOString().split('T')[0]}.pdf`);
      toast.success('Insight exported as PDF!');
    } catch (error) {
      console.error('Error exporting insight:', error);
      toast.error('Failed to export insight. Make sure pop-ups are allowed.');
    }
  };

  const handleSuggestTags = async () => {
    if (!newReflection.trim() || newReflection.length < 20) {
      toast.info('Write at least a few sentences to get tag suggestions');
      return;
    }

    setIsLoadingTags(true);
    try {
      const response = await axios.post(
        `${API}/reflections/suggest-tags`,
        { text: newReflection },
        { withCredentials: true }
      );
      
      if (response.data.tags && response.data.tags.length > 0) {
        setSuggestedTags(response.data.tags);
        toast.success('Tag suggestions ready!');
      } else {
        toast.info('No tag suggestions available');
      }
    } catch (error) {
      console.error('Error suggesting tags:', error);
      toast.error('Failed to suggest tags');
    } finally {
      setIsLoadingTags(false);
    }
  };

  const handleAddSuggestedTag = (tag) => {
    const currentTags = tags.split(',').map(t => t.trim()).filter(t => t);
    if (!currentTags.includes(tag)) {
      setTags(currentTags.concat(tag).join(', '));
    }
    setSuggestedTags(suggestedTags.filter(t => t !== tag));
  };

  const handleNextPrompt = () => {
    setCurrentPromptIndex((currentPromptIndex + 1) % REFLECTION_PROMPTS.length);
  };

  const handleExportReflections = async () => {
    try {
      const response = await axios.get(`${API}/reflections/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reflections_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel file downloaded! Open in Excel or Google Sheets.');
    } catch (error) {
      console.error('Error exporting reflections:', error);
      toast.error('Failed to export reflections');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchInput('');
    setSelectedTag('');
    setSelectedSentiment('');
  };

  const handleSearch = () => {
    console.log('Search button clicked, input value:', searchInput);
    setSearchQuery(searchInput);
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'bg-green-100 text-green-800 border-green-200';
      case 'negative': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-accent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-accent/5 via-brand-accent/5 to-blue-50">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center" data-testid="brand-logo">
              <img
                src="/logo.png"
                alt="ReflectiveMind"
                className="h-12 sm:h-14 w-auto"
              />
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button
                onClick={() => setShowReflectivePractitionerGuide(true)}
                variant="outline"
                size="sm"
                className="border-brand-border text-brand-accent hover:bg-brand-accent/5 hidden lg:flex"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                What is a Reflective Practitioner
              </Button>
              <Button
                onClick={() => setShowReflectivePractitionerGuide(true)}
                variant="outline"
                size="sm"
                className="border-brand-border text-brand-accent hover:bg-brand-accent/5 lg:hidden"
                title="What is a Reflective Practitioner"
              >
                <Lightbulb className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setShowUserGuide(true)}
                variant="outline"
                size="sm"
                className="border-brand-border text-brand-accent hover:bg-brand-accent/5 hidden md:flex"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                User Guide
              </Button>
              <Button
                onClick={() => setShowUserGuide(true)}
                variant="outline"
                size="sm"
                className="border-brand-border text-brand-accent hover:bg-brand-accent/5 md:hidden"
                title="User Guide"
              >
                <BookOpen className="w-4 h-4" />
              </Button>
              {user && (
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {subscription && subscription.monetization_enabled && (
                    <div className="hidden md:flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold"
                         style={{
                           backgroundColor: 
                             subscription.subscription_tier === 'premium' || subscription.subscription_tier === 'grandfathered' 
                               ? '#f3e8ff' 
                               : subscription.subscription_tier === 'trial' 
                               ? '#fef3c7' 
                               : '#f3f4f6',
                           color:
                             subscription.subscription_tier === 'premium' || subscription.subscription_tier === 'grandfathered'
                               ? '#7c3aed'
                               : subscription.subscription_tier === 'trial'
                               ? '#d97706'
                               : '#6b7280'
                         }}>
                      {(subscription.subscription_tier === 'premium' || subscription.subscription_tier === 'grandfathered') && (
                        <Crown className="w-3 h-3" />
                      )}
                      <span className="capitalize">
                        {subscription.subscription_tier === 'grandfathered' ? 'Premium' : subscription.subscription_tier}
                      </span>
                      {subscription.subscription_tier === 'trial' && subscription.days_remaining !== null && (
                        <span className="ml-1">({subscription.days_remaining}d left)</span>
                      )}
                    </div>
                  )}
                  <img src={user.picture} alt={user.name} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-brand-accent" />
                  <span className="text-gray-700 font-medium hidden lg:inline text-sm">{user.name}</span>
                </div>
              )}
              <Button 
                data-testid="logout-btn"
                onClick={handleLogout} 
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-gray-900"
                title="Logout"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Subscription Banner */}
        <SubscriptionBanner 
          user={user} 
          onUpgradeClick={() => setShowUpgradeModal(true)} 
        />

        {/* Add-to-home-screen prompt (PWA install) */}
        <InstallPrompt />

        {/* Daily inspirational quote — rotates by day-of-year */}
        <DailyQuote />
        
        <Tabs defaultValue="reflect" className="space-y-4 sm:space-y-8">
          <TabsList className="bg-white/70 backdrop-blur-md border border-gray-200 p-1 w-full flex overflow-x-auto">
            <TabsTrigger data-testid="reflect-tab" value="reflect" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white text-xs sm:text-sm flex-1 min-w-fit px-2 sm:px-4">Reflect</TabsTrigger>
            <TabsTrigger data-testid="insights-tab" value="insights" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white text-xs sm:text-sm flex-1 min-w-fit px-2 sm:px-4">{user?.app_mode === 'lite' ? 'Weekly Insight' : 'Insights'}</TabsTrigger>
            {user?.app_mode !== 'lite' && (
              <>
                <TabsTrigger data-testid="reviews-tab" value="reviews" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white text-xs sm:text-sm flex-1 min-w-fit px-2 sm:px-4">Reviews</TabsTrigger>
                <TabsTrigger data-testid="action-plans-tab" value="action-plans" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white text-xs sm:text-sm flex-1 min-w-fit px-2 sm:px-4">Plans</TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Reflect Tab */}
          <TabsContent value="reflect" className="space-y-4 sm:space-y-8">
            {/* Sentiment Trend Chart */}
            <div>
              <SentimentTrendChart data={sentimentTrend} />
            </div>
            
            {/* New Reflection Card */}
            <Card className="glass border-gray-200 shadow-lg">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div>
                    <h2 className="section-title mb-2 text-xl sm:text-2xl">New Reflection</h2>
                    <CardDescription className="text-sm">Capture your thoughts, experiences, and learnings</CardDescription>
                  </div>
                  <Button
                    onClick={handleNextPrompt}
                    variant="outline"
                    size="sm"
                    className="text-brand-accent border-brand-border hover:bg-brand-accent/5 w-full sm:w-auto min-h-[44px]"
                    data-testid="change-prompt-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    New Prompt
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  data-testid="reflection-textarea"
                  placeholder={REFLECTION_PROMPTS[currentPromptIndex]}
                  value={newReflection}
                  onChange={(e) => setNewReflection(e.target.value)}
                  className="min-h-[150px] border-gray-300 focus:border-brand-accent text-base"
                  spellCheck
                />
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Input
                      data-testid="tags-input"
                      placeholder="Tags: work, confidence, learning"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="border-gray-300 focus:border-brand-accent flex-1 min-h-[44px] text-base"
                      spellCheck
                    />
                    <Button
                      onClick={handleSuggestTags}
                      variant="outline"
                      size="sm"
                      disabled={isLoadingTags}
                      className="text-brand-accent border-brand-border min-h-[44px] w-full sm:w-auto whitespace-nowrap"
                      data-testid="suggest-tags-btn"
                    >
                      <Lightbulb className="w-4 h-4 mr-1" />
                      {isLoadingTags ? 'Suggesting...' : 'Suggest Tags'}
                    </Button>
                  </div>
                  {suggestedTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm text-gray-600">Suggested:</span>
                      {suggestedTags.map((tag, idx) => (
                        <Badge
                          key={idx}
                          onClick={() => handleAddSuggestedTag(tag)}
                          className="cursor-pointer bg-brand-accent/10 text-brand-accent border-brand-border hover:bg-brand-accent/15"
                        >
                          + {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-4">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
                    <Button
                      data-testid="record-btn"
                      onClick={isRecording ? stopRecording : startRecording}
                      variant="outline"
                      className={`${isRecording ? 'bg-red-50 border-red-300 animate-pulse' : 'border-gray-300'} min-h-[44px] w-full sm:w-auto`}
                    >
                      {isRecording ? <MicOff className="w-5 h-5 text-red-600" /> : <Mic className="w-5 h-5" />}
                      <span className="ml-2">{isRecording ? 'Stop Recording' : 'Voice Note'}</span>
                    </Button>
                    <Button 
                      data-testid="save-reflection-btn"
                      onClick={handleCreateReflection}
                      className="bg-brand-accent hover:bg-brand-accent text-white min-h-[44px] w-full sm:w-auto"
                    >
                      Save Reflection
                    </Button>
                  </div>
                  
                  {/* Live transcription preview */}
                  {isRecording && voiceTranscript && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 font-medium mb-1">Live Transcription:</p>
                      <p className="text-sm text-gray-700">{voiceTranscript}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Reflections */}
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
                <h2 className="section-title text-xl sm:text-2xl">Your Reflections</h2>
                <Button
                  onClick={handleExportReflections}
                  variant="outline"
                  className="text-brand-accent border-brand-border hover:bg-brand-accent/5 min-h-[44px] w-full sm:w-auto"
                  data-testid="export-reflections-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export to Excel
                </Button>
              </div>
              
              {/* Filters */}
              <Card className="glass border-gray-200 shadow-lg mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                  <div className="flex flex-col gap-4">
                    <div className="w-full">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Search Text</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            placeholder="Search in reflection text..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            className="pl-10 border-gray-300 min-h-[44px] text-base"
                            data-testid="search-input"
                          />
                        </div>
                        <Button
                          onClick={handleSearch}
                          className="bg-brand-accent hover:bg-brand-accent text-white min-h-[44px] w-full sm:w-auto"
                          data-testid="search-btn"
                        >
                          Search
                        </Button>
                      </div>
                    </div>
                    
                    <div className="w-full sm:w-48">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Filter by Tag</label>
                      <select
                        value={selectedTag}
                        onChange={(e) => setSelectedTag(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent min-h-[44px] text-base"
                        data-testid="tag-filter"
                      >
                        <option value="">All tags</option>
                        {allTags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="w-full sm:w-40">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Sentiment</label>
                      <select
                        value={selectedSentiment}
                        onChange={(e) => setSelectedSentiment(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent min-h-[44px] text-base"
                        data-testid="sentiment-filter"
                      >
                        <option value="">All</option>
                        <option value="positive">Positive</option>
                        <option value="neutral">Neutral</option>
                        <option value="negative">Negative</option>
                      </select>
                    </div>
                    
                    {(searchQuery || selectedTag || selectedSentiment) && (
                      <Button
                        onClick={handleClearFilters}
                        variant="outline"
                        size="sm"
                        className="text-gray-600 min-h-[44px] w-full sm:w-auto"
                        data-testid="clear-filters-btn"
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                  
                  {(searchQuery || selectedTag || selectedSentiment) && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-blue-900">
                          <span className="font-semibold">Showing {reflections.length} result{reflections.length !== 1 ? 's' : ''}</span>
                          <span className="mx-2">•</span>
                          <span className="font-medium">Active filters:</span>
                          {searchQuery && <Badge variant="outline" className="ml-2 bg-white">Text: &quot;{searchQuery}&quot;</Badge>}
                          {selectedTag && <Badge variant="outline" className="ml-2 bg-white">Tag: {selectedTag}</Badge>}
                          {selectedSentiment && <Badge variant="outline" className="ml-2 bg-white">Sentiment: {selectedSentiment}</Badge>}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <div className="grid gap-6">
                {reflections.length === 0 ? (
                  <Card className="glass border-gray-200">
                    <CardContent className="py-12 text-center text-gray-500">
                      {(searchQuery || selectedTag || selectedSentiment) ? (
                        <div>
                          <p className="text-lg font-medium mb-2">No reflections match your search criteria</p>
                          <p className="text-sm">Try adjusting your filters or search terms</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-lg font-medium mb-2">No reflections yet</p>
                          <p className="text-sm">Start by writing your first reflection above!</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  reflections.map((reflection) => (
                    <Card key={reflection.id} className="glass border-gray-200 shadow-lg reflection-card" data-testid={`reflection-card-${reflection.id}`}>
                      <CardContent className="pt-6">
                        {editingReflection === reflection.id ? (
                          // Edit mode
                          <div className="space-y-4">
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              className="min-h-[150px] border-gray-300 focus:border-brand-accent"
                              spellCheck
                            />
                            <Input
                              placeholder="Tags (comma separated)"
                              value={editTags}
                              onChange={(e) => setEditTags(e.target.value)}
                              className="border-gray-300"
                              spellCheck
                            />
                            <div className="flex justify-end space-x-2">
                              <Button
                                onClick={handleCancelEdit}
                                variant="outline"
                                size="sm"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => handleSaveEdit(reflection.id)}
                                className="bg-brand-accent hover:bg-brand-accent-hover text-white"
                                size="sm"
                              >
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <>
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center space-x-2">
                                <Badge className={getSentimentColor(reflection.sentiment)}>
                                  {reflection.sentiment}
                                </Badge>
                                {reflection.tags.map((tag, idx) => (
                                  <Badge key={idx} variant="outline" className="border-brand-accent/30 text-brand-accent">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex space-x-2">
                                <Button
                                  data-testid={`edit-reflection-${reflection.id}`}
                                  onClick={() => handleEditReflection(reflection)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  <span className="text-sm">Edit</span>
                                </Button>
                                <Button
                                  data-testid={`delete-reflection-${reflection.id}`}
                                  onClick={() => handleDeleteReflection(reflection.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap mb-4">{reflection.text}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(reflection.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-4 sm:space-y-8">
            {/* Sub tabs for weekly and monthly insights */}
            <Tabs defaultValue="weekly" className="space-y-6">
              <TabsList className="bg-brand-accent/5 border border-brand-border w-full flex flex-wrap overflow-x-auto">
                <TabsTrigger value="weekly" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white flex-1 min-w-fit text-xs sm:text-sm px-2 sm:px-4">
                  Weekly
                </TabsTrigger>
                {user?.app_mode !== 'lite' && (
                  <>
                    <TabsTrigger value="monthly" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white flex-1 min-w-fit text-xs sm:text-sm px-2 sm:px-4">
                      Monthly
                    </TabsTrigger>
                    <TabsTrigger value="quarterly" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white flex-1 min-w-fit text-xs sm:text-sm px-2 sm:px-4">
                      Quarterly
                    </TabsTrigger>
                    <TabsTrigger value="yearly" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white flex-1 min-w-fit text-xs sm:text-sm px-2 sm:px-4">
                      Yearly
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
              
              {/* Info Banner — only meaningful when Monthly+ are visible */}
              {user?.app_mode !== 'lite' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-xs sm:text-sm font-semibold text-brand-ink mb-1">Keep your insights</h4>
                  <p className="text-xs sm:text-sm text-brand-cta">
                    Quarterly insights require 3 monthly insights. Yearly insights require 4 quarterly or 12 monthly insights. Keep your insights to generate longer-term analysis.
                  </p>
                </div>
              </div>
              )}

              {/* Lite users get no extra banner here — the Reflect tab + Weekly Insight
                  speak for themselves. */}

              {/* Weekly Insights */}
              <TabsContent value="weekly" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">Weekly Insights</h3>
                  <div className="flex flex-col items-end">
                    <Button
                      data-testid="generate-weekly-insights-btn"
                      onClick={() => handleGenerateInsights('weekly')}
                      disabled={generatingInsights || !canGenerateWeeklyInsights}
                      className="bg-brand-accent hover:bg-brand-accent-hover text-white disabled:opacity-50 min-h-[44px]"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generatingInsights ? 'Generating...' : 'Generate Insights'}
                    </Button>
                    {!canGenerateWeeklyInsights && (
                      <p className="text-xs text-orange-600 mt-1">Already generated for this week</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-6">
                  {weeklyInsights.length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        No weekly insights yet. Generate insights from your past 7 days of reflections!
                      </CardContent>
                    </Card>
                  ) : (
                    weeklyInsights.map((insight, wIdx) => {
                      const isExpanded = weeklyExpanded[insight.id] ?? (wIdx === 0);
                      return (
                      <Card key={insight.id} className="glass border-gray-200 shadow-lg" data-testid={`insight-card-${insight.id}`}>
                        <CardHeader
                          onClick={() => setWeeklyExpanded(prev => ({ ...prev, [insight.id]: !isExpanded }))}
                          className="cursor-pointer select-none"
                          data-testid={`insight-toggle-${insight.id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 text-sm leading-none mt-1">{isExpanded ? '▾' : '▸'}</span>
                              <div>
                                <CardTitle className="flex items-center space-x-2">
                                  <TrendingUp className="w-6 h-6 text-brand-accent" />
                                  <span>Weekly Insights</span>
                                  {wIdx === 0 && (
                                    <Badge className="ml-2 bg-brand-accent/10 text-brand-accent border-brand-border text-[10px] uppercase tracking-wider">
                                      Latest
                                    </Badge>
                                  )}
                                </CardTitle>
                                <CardDescription>
                                  {new Date(insight.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </CardDescription>
                              </div>
                            </div>
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button
                                onClick={() => handleExportInsight(insight)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                title="Export as PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteInsight(insight.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                title="Delete Insight"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                    {isExpanded && (
                    <CardContent className="space-y-6">
                      {/* Themes */}
                      {insight.themes.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Key Themes:</h4>
                          <div className="flex flex-wrap gap-2">
                            {insight.themes.map((theme, idx) => (
                              <Badge key={idx} className="bg-brand-accent-soft text-brand-accent border-brand-border">
                                {theme}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Summary:</h4>
                        <p className="text-gray-700 whitespace-pre-wrap">{insight.summary}</p>
                      </div>

                      {/* Sentiment Trend */}
                      {Object.keys(insight.sentiment_trend).length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Emotional Trend:</h4>
                          <div className="flex space-x-4">
                            {Object.entries(insight.sentiment_trend).map(([sentiment, count]) => (
                              <Badge key={sentiment} className={getSentimentColor(sentiment)}>
                                {sentiment}: {count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Word Frequency */}
                      {Object.keys(insight.word_frequency).length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Frequent Words:</h4>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(insight.word_frequency).map(([word, count]) => (
                              <Badge key={word} variant="outline" className="border-gray-300">
                                {word} ({count})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Plan Suggestion — text always shown when the
                          AI produced one. The "Create Action Plan" button (which
                          inserts it into the Action Plans tab) is hidden for
                          Lite users since they have no Action Plans tab. */}
                      {insight.suggested_action_plan && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-gray-900 mb-2">💡 Suggested Action Plan:</h4>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                            <h5 className="font-medium text-blue-900 mb-2">{insight.suggested_action_plan.title}</h5>
                            <p className="text-blue-800 text-sm mb-3">{insight.suggested_action_plan.description}</p>
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-blue-700 mb-1">Suggested Tasks:</p>
                              {insight.suggested_action_plan.tasks.map((task, idx) => (
                                <div key={idx} className="text-sm text-blue-700 flex items-start">
                                  <span className="mr-2">•</span>
                                  <span>{task}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {user?.app_mode !== 'lite' && (
                            <Button
                              onClick={() => handleCreateActionPlanFromInsight(insight.id)}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              size="sm"
                            >
                              <Target className="w-4 h-4 mr-2" />
                              Create Action Plan
                            </Button>
                          )}
                        </div>
                      )}
                        </CardContent>
                    )}
                      </Card>
                    );
                    })
                  )}
                </div>
              </TabsContent>

              {/* Monthly Insights */}
              <TabsContent value="monthly" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">Monthly Insights</h3>
                  <div className="flex flex-col items-end">
                    <Button
                      data-testid="generate-monthly-insights-btn"
                      onClick={() => handleGenerateInsights('monthly')}
                      disabled={generatingInsights || !canGenerateMonthlyInsights}
                      className="bg-brand-accent hover:bg-brand-accent-hover text-white disabled:opacity-50 min-h-[44px]"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generatingInsights ? 'Generating...' : 'Generate Insights'}
                    </Button>
                    {!canGenerateMonthlyInsights && (
                      <p className="text-xs text-orange-600 mt-1">Already generated for this month</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-6">
                  {monthlyInsights.length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        No monthly insights yet. Generate insights from your past month of reflections!
                      </CardContent>
                    </Card>
                  ) : (
                    monthlyInsights.map((insight) => (
                      <Card key={insight.id} className="glass border-gray-200 shadow-lg" data-testid={`insight-card-${insight.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="flex items-center space-x-2">
                                <TrendingUp className="w-6 h-6 text-brand-accent" />
                                <span>Monthly Insights</span>
                              </CardTitle>
                              <CardDescription>
                                {new Date(insight.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleExportInsight(insight)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                title="Export as PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteInsight(insight.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                title="Delete Insight"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          {/* Themes */}
                          {insight.themes.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Key Themes:</h4>
                              <div className="flex flex-wrap gap-2">
                                {insight.themes.map((theme, idx) => (
                                  <Badge key={idx} className="bg-brand-accent-soft text-brand-accent border-brand-border">
                                    {theme}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Summary */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Summary:</h4>
                            <p className="text-gray-700 whitespace-pre-wrap">{insight.summary}</p>
                          </div>

                          {/* Sentiment Trend */}
                          {Object.keys(insight.sentiment_trend).length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Emotional Trend:</h4>
                              <div className="flex space-x-4">
                                {Object.entries(insight.sentiment_trend).map(([sentiment, count]) => (
                                  <Badge key={sentiment} className={getSentimentColor(sentiment)}>
                                    {sentiment}: {count}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Word Frequency */}
                          {Object.keys(insight.word_frequency).length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Frequent Words:</h4>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(insight.word_frequency).map(([word, count]) => (
                                  <Badge key={word} variant="outline" className="border-gray-300">
                                    {word} ({count})
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Action Plan Suggestion */}
                          {insight.suggested_action_plan && (
                            <div className="border-t pt-4">
                              <h4 className="font-semibold text-gray-900 mb-2">💡 Suggested Action Plan:</h4>
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                <h5 className="font-medium text-blue-900 mb-2">{insight.suggested_action_plan.title}</h5>
                                <p className="text-blue-800 text-sm mb-3">{insight.suggested_action_plan.description}</p>
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-blue-700 mb-1">Suggested Tasks:</p>
                                  {insight.suggested_action_plan.tasks.map((task, idx) => (
                                    <div key={idx} className="text-sm text-blue-700 flex items-start">
                                      <span className="mr-2">•</span>
                                      <span>{task}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <Button
                                onClick={() => handleCreateActionPlanFromInsight(insight.id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                size="sm"
                              >
                                <Target className="w-4 h-4 mr-2" />
                                Create Action Plan
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Quarterly Insights */}
              <TabsContent value="quarterly" className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Quarterly Insights (3 Months)</h3>
                  <div className="flex flex-col items-start sm:items-end">
                    <Button
                      data-testid="generate-quarterly-insights-btn"
                      onClick={() => handleGenerateInsights('quarterly')}
                      disabled={generatingInsights || !canGenerateQuarterlyInsights}
                      className="bg-brand-accent hover:bg-brand-accent-hover text-white disabled:opacity-50 min-h-[44px] w-full sm:w-auto"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generatingInsights ? 'Generating...' : 'Generate Insights'}
                    </Button>
                    {!canGenerateQuarterlyInsights && (
                      <p className="text-xs text-orange-600 mt-1">Already generated for this quarter</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-6">
                  {quarterlyInsights.length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        <p className="mb-2">No quarterly insights yet.</p>
                        <p className="text-sm">Requires 3 monthly insights to generate a quarterly meta-analysis.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    quarterlyInsights.map((insight) => (
                      <Card key={insight.id} className="glass border-gray-200 shadow-lg bg-gradient-to-br from-brand-accent/5 to-brand-accent/5" data-testid={`insight-card-${insight.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="flex items-center space-x-2">
                                <TrendingUp className="w-6 h-6 text-brand-accent" />
                                <span>Quarterly Insights</span>
                              </CardTitle>
                              <CardDescription>
                                {new Date(insight.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleExportInsight(insight)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                title="Export as PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteInsight(insight.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                title="Delete Insight"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          {/* Key Themes */}
                          {insight.themes.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Key Themes:</h4>
                              <div className="flex flex-wrap gap-2">
                                {insight.themes.map((theme, idx) => (
                                  <Badge key={idx} className="bg-brand-accent-soft text-brand-accent border-brand-border">
                                    {theme}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Summary */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Overall Summary:</h4>
                            <p className="text-gray-700 whitespace-pre-wrap">{insight.summary}</p>
                          </div>

                          {/* Enhanced Analytics */}
                          {insight.trend_analysis && (
                            <div className="bg-brand-accent/5 border border-brand-border rounded-lg p-4">
                              <h4 className="font-semibold text-brand-ink mb-2 flex items-center">
                                <TrendingUp className="w-4 h-4 mr-2" />
                                Trend Analysis
                              </h4>
                              <p className="text-brand-accent text-sm whitespace-pre-wrap">{insight.trend_analysis.analysis}</p>
                            </div>
                          )}

                          {insight.growth_tracking && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                                <Target className="w-4 h-4 mr-2" />
                                Growth Tracking
                              </h4>
                              <p className="text-green-800 text-sm whitespace-pre-wrap mb-3">{insight.growth_tracking.analysis}</p>
                              {insight.action_plan_success && (
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                  <div className="bg-white rounded p-2">
                                    <div className="text-gray-600">Plans Completed</div>
                                    <div className="text-lg font-bold text-green-600">{insight.action_plan_success.closed_plans}/{insight.action_plan_success.total_plans}</div>
                                  </div>
                                  <div className="bg-white rounded p-2">
                                    <div className="text-gray-600">Tasks Completed</div>
                                    <div className="text-lg font-bold text-green-600">{insight.action_plan_success.completed_tasks}/{insight.action_plan_success.total_tasks}</div>
                                  </div>
                                  <div className="bg-white rounded p-2 col-span-2">
                                    <div className="text-gray-600">Completion Rate</div>
                                    <div className="text-lg font-bold text-green-600">{insight.action_plan_success.completion_rate}%</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {insight.pattern_comparison && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="font-semibold text-blue-900 mb-2">Pattern Comparison</h4>
                              <p className="text-blue-800 text-sm whitespace-pre-wrap">{insight.pattern_comparison.analysis}</p>
                            </div>
                          )}

                          {insight.peak_insights && insight.peak_insights.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <h4 className="font-semibold text-yellow-900 mb-2">⭐ Peak Insights</h4>
                              <ul className="space-y-2">
                                {insight.peak_insights.map((peak, idx) => (
                                  <li key={idx} className="text-yellow-800 text-sm flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{peak}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Action Plan Suggestion */}
                          {insight.suggested_action_plan && (
                            <div className="border-t pt-4">
                              <h4 className="font-semibold text-gray-900 mb-2">💡 Suggested Action Plan:</h4>
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                <h5 className="font-medium text-blue-900 mb-2">{insight.suggested_action_plan.title}</h5>
                                <p className="text-blue-800 text-sm mb-3">{insight.suggested_action_plan.description}</p>
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-blue-700 mb-1">Suggested Tasks:</p>
                                  {insight.suggested_action_plan.tasks.map((task, idx) => (
                                    <div key={idx} className="text-sm text-blue-700 flex items-start">
                                      <span className="mr-2">•</span>
                                      <span>{task}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <Button
                                onClick={() => handleCreateActionPlanFromInsight(insight.id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                size="sm"
                              >
                                <Target className="w-4 h-4 mr-2" />
                                Create Action Plan
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Yearly Insights */}
              <TabsContent value="yearly" className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Yearly Insights (12 Months)</h3>
                  <div className="flex flex-col items-start sm:items-end">
                    <Button
                      data-testid="generate-yearly-insights-btn"
                      onClick={() => handleGenerateInsights('yearly')}
                      disabled={generatingInsights || !canGenerateYearlyInsights}
                      className="bg-brand-accent hover:bg-brand-accent-hover text-white disabled:opacity-50 min-h-[44px] w-full sm:w-auto"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generatingInsights ? 'Generating...' : 'Generate Insights'}
                    </Button>
                    {!canGenerateYearlyInsights && (
                      <p className="text-xs text-orange-600 mt-1">Already generated for this year</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-6">
                  {yearlyInsights.length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        <p className="mb-2">No yearly insights yet.</p>
                        <p className="text-sm">Requires 4 quarterly insights or 12 monthly insights to generate a yearly meta-analysis.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    yearlyInsights.map((insight) => (
                      <Card key={insight.id} className="glass border-gray-200 shadow-lg bg-gradient-to-br from-brand-accent/5 to-pink-50" data-testid={`insight-card-${insight.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="flex items-center space-x-2">
                                <TrendingUp className="w-6 h-6 text-brand-accent" />
                                <span>Yearly Insights</span>
                              </CardTitle>
                              <CardDescription>
                                {new Date(insight.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleExportInsight(insight)}
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700"
                                title="Export as PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteInsight(insight.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                title="Delete Insight"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          {/* Similar structure to quarterly but with yearly-specific display */}
                          {insight.themes.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Key Themes This Year:</h4>
                              <div className="flex flex-wrap gap-2">
                                {insight.themes.map((theme, idx) => (
                                  <Badge key={idx} className="bg-brand-accent/10 text-brand-accent border-brand-border">
                                    {theme}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Year in Review:</h4>
                            <p className="text-gray-700 whitespace-pre-wrap">{insight.summary}</p>
                          </div>

                          {insight.trend_analysis && (
                            <div className="bg-brand-accent/5 border border-brand-border rounded-lg p-4">
                              <h4 className="font-semibold text-brand-ink mb-2 flex items-center">
                                <TrendingUp className="w-4 h-4 mr-2" />
                                Trend Analysis
                              </h4>
                              <p className="text-brand-accent text-sm whitespace-pre-wrap">{insight.trend_analysis.analysis}</p>
                            </div>
                          )}

                          {insight.growth_tracking && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                                <Target className="w-4 h-4 mr-2" />
                                Annual Growth
                              </h4>
                              <p className="text-green-800 text-sm whitespace-pre-wrap mb-3">{insight.growth_tracking.analysis}</p>
                              {insight.action_plan_success && (
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                  <div className="bg-white rounded p-2">
                                    <div className="text-gray-600">Plans Completed</div>
                                    <div className="text-lg font-bold text-green-600">{insight.action_plan_success.closed_plans}/{insight.action_plan_success.total_plans}</div>
                                  </div>
                                  <div className="bg-white rounded p-2">
                                    <div className="text-gray-600">Tasks Completed</div>
                                    <div className="text-lg font-bold text-green-600">{insight.action_plan_success.completed_tasks}/{insight.action_plan_success.total_tasks}</div>
                                  </div>
                                  <div className="bg-white rounded p-2 col-span-2">
                                    <div className="text-gray-600">Overall Completion</div>
                                    <div className="text-lg font-bold text-green-600">{insight.action_plan_success.completion_rate}%</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {insight.seasonal_patterns && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                              <h4 className="font-semibold text-orange-900 mb-2">📅 Seasonal Patterns</h4>
                              <p className="text-orange-800 text-sm whitespace-pre-wrap">{insight.seasonal_patterns.analysis}</p>
                            </div>
                          )}

                          {insight.peak_insights && insight.peak_insights.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <h4 className="font-semibold text-yellow-900 mb-2">⭐ Year&apos;s Peak Moments</h4>
                              <ul className="space-y-2">
                                {insight.peak_insights.map((peak, idx) => (
                                  <li key={idx} className="text-yellow-800 text-sm flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{peak}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Action Plan Suggestion */}
                          {insight.suggested_action_plan && (
                            <div className="border-t pt-4">
                              <h4 className="font-semibold text-gray-900 mb-2">💡 Strategic Action Plan for Next Year:</h4>
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                                <h5 className="font-medium text-blue-900 mb-2">{insight.suggested_action_plan.title}</h5>
                                <p className="text-blue-800 text-sm mb-3">{insight.suggested_action_plan.description}</p>
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-blue-700 mb-1">Suggested Tasks:</p>
                                  {insight.suggested_action_plan.tasks.map((task, idx) => (
                                    <div key={idx} className="text-sm text-blue-700 flex items-start">
                                      <span className="mr-2">•</span>
                                      <span>{task}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <Button
                                onClick={() => handleCreateActionPlanFromInsight(insight.id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                size="sm"
                              >
                                <Target className="w-4 h-4 mr-2" />
                                Create Action Plan
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-4 sm:space-y-8">
            {/* Sub tabs for different review types and analytics */}
            <Tabs defaultValue="weekly" className="space-y-6">
              <TabsList className="bg-brand-accent/5 border border-brand-border">
                <TabsTrigger value="weekly" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                  Weekly Reviews
                </TabsTrigger>
                <TabsTrigger value="monthly" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                  Monthly Reviews
                </TabsTrigger>
                <TabsTrigger value="trends" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                  Emotional Trends
                </TabsTrigger>
              </TabsList>

              {/* Weekly Reviews */}
              <TabsContent value="weekly" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">Weekly Reviews</h3>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      onClick={() => handleGenerateReviewTemplate('weekly')}
                      disabled={generatingReview || !canCreateWeeklyReview}
                      className="bg-brand-accent hover:bg-brand-accent text-white disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {generatingReview ? 'Generating...' : 'Create Weekly Review'}
                    </Button>
                    {!canCreateWeeklyReview && (
                      <p className="text-xs text-orange-600">Already created for this week</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-6">
                  {weeklyReviews.length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        No weekly reviews yet. Create your first review to reflect on your week!
                      </CardContent>
                    </Card>
                  ) : (
                    weeklyReviews.map((review) => (
                      <Card key={review.id} className="glass border-gray-200 shadow-lg">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="flex items-center space-x-2">
                                <FileText className="w-6 h-6 text-brand-accent" />
                                <span>Week of {new Date(review.period_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                              </CardTitle>
                              <CardDescription>
                                Created: {new Date(review.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              {editingReview === review.id ? (
                                <>
                                  <Button
                                    onClick={handleSaveEditReview}
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={handleCancelEditReview}
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-600 hover:text-gray-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    onClick={() => handleEditReview(review)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-brand-accent hover:text-brand-accent"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleExportReview(review.id, 'pdf')}
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 hover:text-blue-700"
                                    title="Export as PDF"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteReview(review.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {editingReview === review.id ? (
                            <Textarea
                              value={editReviewContent}
                              onChange={(e) => setEditReviewContent(e.target.value)}
                              className="min-h-[300px] font-mono text-sm"
                              spellCheck
                            />
                          ) : (
                            <div className="prose prose-sm max-w-none">
                              <ReactMarkdown>{review.content}</ReactMarkdown>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Monthly Reviews */}
              <TabsContent value="monthly" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">Monthly Reviews</h3>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      onClick={() => handleGenerateReviewTemplate('monthly')}
                      disabled={generatingReview || !canCreateMonthlyReview}
                      className="bg-brand-accent hover:bg-brand-accent text-white disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {generatingReview ? 'Generating...' : 'Create Monthly Review'}
                    </Button>
                    {!canCreateMonthlyReview && (
                      <p className="text-xs text-orange-600">Already created for this month</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-6">
                  {monthlyReviews.length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        No monthly reviews yet. Create your first review to reflect on your month!
                      </CardContent>
                    </Card>
                  ) : (
                    monthlyReviews.map((review) => (
                      <Card key={review.id} className="glass border-gray-200 shadow-lg">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="flex items-center space-x-2">
                                <FileText className="w-6 h-6 text-brand-accent" />
                                <span>{new Date(review.period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                              </CardTitle>
                              <CardDescription>
                                Created: {new Date(review.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              {editingReview === review.id ? (
                                <>
                                  <Button
                                    onClick={handleSaveEditReview}
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={handleCancelEditReview}
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-600 hover:text-gray-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    onClick={() => handleEditReview(review)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-brand-accent hover:text-brand-accent"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleExportReview(review.id, 'excel')}
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700"
                                    title="Export as Excel"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteReview(review.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {editingReview === review.id ? (
                            <Textarea
                              value={editReviewContent}
                              onChange={(e) => setEditReviewContent(e.target.value)}
                              className="min-h-[300px] font-mono text-sm"
                              spellCheck
                            />
                          ) : (
                            <div className="prose prose-sm max-w-none">
                              <ReactMarkdown>{review.content}</ReactMarkdown>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Emotional Trends */}
              <TabsContent value="trends" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900">Emotional Trends Over Time</h3>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleToggleEmotionalTrendsPeriod('weekly')}
                      variant="outline"
                      size="sm"
                      className="border-brand-border text-brand-accent hover:bg-brand-accent/5"
                    >
                      Weekly View
                    </Button>
                    <Button
                      onClick={() => handleToggleEmotionalTrendsPeriod('monthly')}
                      variant="outline"
                      size="sm"
                      className="border-brand-border text-brand-accent hover:bg-brand-accent/5"
                    >
                      Monthly View
                    </Button>
                  </div>
                </div>

                <Card className="glass border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <BarChart3 className="w-6 h-6 text-brand-accent" />
                      <span>Sentiment Trends</span>
                    </CardTitle>
                    <CardDescription>
                      Track how your emotional patterns change over time
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <EmotionalTrendsChart trends={emotionalTrends} periodType="weekly" />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Action Plans Tab */}
          <TabsContent value="action-plans" className="space-y-4 sm:space-y-8">
            {/* Statistics Dashboard */}
            {actionPlanStats && <ActionPlanStatsChart stats={actionPlanStats} />}

            {/* Header with Create Button and Sort */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className="section-title text-xl sm:text-2xl">Action Plans</h2>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <select
                  value={actionPlanSort}
                  onChange={(e) => setActionPlanSort(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent min-h-[44px] text-base"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
                <Dialog open={showActionPlanDialog} onOpenChange={setShowActionPlanDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-action-plan-btn" className="bg-brand-accent hover:bg-brand-accent text-white min-h-[44px] w-full sm:w-auto">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Plan
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white max-w-md">
                    <DialogHeader>
                      <DialogTitle>Create Action Plan</DialogTitle>
                      <DialogDescription>
                        Transform your insights into actionable goals
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <Input
                        data-testid="action-plan-title-input"
                        placeholder="Plan title (e.g., Improve Confidence)"
                        value={newActionPlan.title}
                        onChange={(e) => setNewActionPlan({ ...newActionPlan, title: e.target.value })}
                        className="border-gray-300 min-h-[44px] text-base"
                        spellCheck
                      />
                      <Textarea
                        data-testid="action-plan-description-textarea"
                        placeholder="Description and goal..."
                        value={newActionPlan.description}
                        onChange={(e) => setNewActionPlan({ ...newActionPlan, description: e.target.value })}
                        className="border-gray-300 text-base"
                        spellCheck
                      />
                      <Textarea
                        data-testid="action-plan-tasks-textarea"
                        placeholder="Tasks (one per line):\n- Speak up in meetings\n- Practice public speaking\n- Join a workshop"
                        value={newActionPlan.tasks}
                        onChange={(e) => setNewActionPlan({ ...newActionPlan, tasks: e.target.value })}
                        className="border-gray-300 min-h-[120px] text-base"
                        spellCheck
                      />
                    </div>
                    <DialogFooter>
                      <Button data-testid="create-plan-submit-btn" onClick={handleCreateActionPlan} className="bg-brand-accent hover:bg-brand-accent text-white min-h-[44px]">
                        Create Plan
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Sub-tabs for Active and Closed Plans */}
            <Tabs defaultValue="active" className="space-y-6">
              <TabsList className="bg-brand-accent/5 border border-brand-border">
                <TabsTrigger value="active" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                  Active Plans
                </TabsTrigger>
                <TabsTrigger value="closed" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                  Closed Plans
                </TabsTrigger>
              </TabsList>

              {/* Active Plans */}
              <TabsContent value="active" className="space-y-6">
                <div className="grid gap-6">
                  {actionPlans.filter(plan => plan.status === 'active').length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        No active action plans. Create your first action plan to start tracking your goals!
                      </CardContent>
                    </Card>
                  ) : (
                    actionPlans.filter(plan => plan.status === 'active').map((plan) => (
                  <Card key={plan.id} className="glass border-gray-200 shadow-lg" data-testid={`action-plan-card-${plan.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {editingActionPlan === plan.id ? (
                            <div className="space-y-3">
                              <Input
                                value={editPlanData.title}
                                onChange={(e) => setEditPlanData({ ...editPlanData, title: e.target.value })}
                                placeholder="Plan title"
                                className="font-semibold"
                                spellCheck
                              />
                              <Textarea
                                value={editPlanData.description}
                                onChange={(e) => setEditPlanData({ ...editPlanData, description: e.target.value })}
                                placeholder="Description"
                                className="min-h-[60px]"
                                spellCheck
                              />
                            </div>
                          ) : (
                            <>
                              <CardTitle className="flex items-center space-x-2 flex-wrap gap-2">
                                <Target className="w-6 h-6 text-brand-accent" />
                                <span>{plan.title}</span>
                                {plan.was_reopened && (
                                  <Badge className="bg-amber-100 text-brand-cta border-amber-200 text-xs">
                                    Reopened • Read-only
                                  </Badge>
                                )}
                              </CardTitle>
                              <CardDescription>{plan.description}</CardDescription>
                            </>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {editingActionPlan === plan.id ? (
                            <>
                              <Button
                                onClick={handleSaveActionPlan}
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={handleCancelEditActionPlan}
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:text-gray-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* Only show Edit button if plan was never reopened */}
                              {!plan.was_reopened && (
                                <Button
                                  data-testid={`edit-action-plan-${plan.id}`}
                                  onClick={() => handleEditActionPlan(plan)}
                                  variant="ghost"
                                  size="sm"
                                  className="text-brand-accent hover:text-brand-accent"
                                  title="Edit plan"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                data-testid={`delete-action-plan-${plan.id}`}
                                onClick={() => handleDeleteActionPlan(plan.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                title="Delete plan"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {editingActionPlan === plan.id && (
                        <div className="bg-brand-accent/5 border border-brand-border rounded-lg p-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-brand-accent" />
                          <span className="text-sm text-brand-accent font-medium">
                            Edit Mode: Make changes and click Save to update, or Cancel to discard
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Progress</span>
                          <span className="text-sm font-semibold text-brand-accent">{Math.round(plan.progress)}%</span>
                        </div>
                        <Progress value={plan.progress} className="h-2" />
                      </div>

                      <div className="space-y-2">
                        {(editingActionPlan === plan.id ? editPlanData.tasks : plan.tasks).map((taskObj, idx) => (
                          <div
                            key={idx}
                            data-testid={`task-${plan.id}-${idx}`}
                            className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
                              editingActionPlan === plan.id 
                                ? 'bg-brand-accent/5/50 border border-brand-border' 
                                : taskObj.completed 
                                  ? 'bg-brand-accent-soft/60 hover:bg-brand-accent-soft border border-brand-border cursor-pointer' 
                                  : 'bg-white/50 hover:bg-white/90 border border-transparent hover:border-brand-accent/30 cursor-pointer'
                            }`}
                          >
                            {editingActionPlan === plan.id ? (
                              // Edit mode: Show checkbox, editable text input, and delete button
                              <>
                                <div 
                                  className="cursor-pointer"
                                  onClick={() => {
                                    const updatedTasks = [...editPlanData.tasks];
                                    updatedTasks[idx] = { ...taskObj, completed: !taskObj.completed };
                                    setEditPlanData({ ...editPlanData, tasks: updatedTasks });
                                  }}
                                >
                                  {taskObj.completed ? (
                                    <CheckCircle2 className="w-5 h-5 text-brand-accent flex-shrink-0 transition-transform duration-200 scale-110" />
                                  ) : (
                                    <Circle className="w-5 h-5 text-brand-sage hover:text-brand-accent flex-shrink-0 transition-all duration-200" />
                                  )}
                                </div>
                                <Input
                                  value={taskObj.task}
                                  onChange={(e) => {
                                    const updatedTasks = [...editPlanData.tasks];
                                    updatedTasks[idx] = { ...taskObj, task: e.target.value };
                                    setEditPlanData({ ...editPlanData, tasks: updatedTasks });
                                  }}
                                  className="flex-1 h-9"
                                  placeholder="Task description"
                                  spellCheck
                                />
                                <Button
                                  onClick={() => {
                                    const updatedTasks = editPlanData.tasks.filter((_, i) => i !== idx);
                                    setEditPlanData({ ...editPlanData, tasks: updatedTasks });
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Delete task"
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              // View mode: Show checkbox and text, clicking toggles completion
                              <>
                                {taskObj.completed ? (
                                  <CheckCircle2 
                                    className="w-5 h-5 text-brand-accent flex-shrink-0 transition-transform duration-200 scale-110 cursor-pointer" 
                                    onClick={() => {
                                      const updatedTasks = [...plan.tasks];
                                      updatedTasks[idx] = { ...plan.tasks[idx], completed: false };
                                      handleToggleTask(plan.id, updatedTasks);
                                    }}
                                  />
                                ) : (
                                  <Circle 
                                    className="w-5 h-5 text-gray-400 hover:text-brand-accent flex-shrink-0 transition-all duration-200 cursor-pointer" 
                                    onClick={() => {
                                      const updatedTasks = [...plan.tasks];
                                      updatedTasks[idx] = { ...plan.tasks[idx], completed: true };
                                      handleToggleTask(plan.id, updatedTasks);
                                    }}
                                  />
                                )}
                                <span className={`flex-1 transition-all duration-200 ${
                                  taskObj.completed 
                                    ? 'line-through text-gray-500' 
                                    : 'text-gray-700 font-medium'
                                }`}>
                                  {taskObj.task}
                                </span>
                              </>
                            )}
                          </div>
                        ))}
                        
                        {/* Add Task button in edit mode */}
                        {editingActionPlan === plan.id && (
                          <Button
                            onClick={() => {
                              const updatedTasks = [...editPlanData.tasks, { task: '', completed: false }];
                              setEditPlanData({ ...editPlanData, tasks: updatedTasks });
                            }}
                            variant="outline"
                            size="sm"
                            className="w-full border-dashed border-brand-border text-brand-accent hover:bg-brand-accent/5 hover:border-brand-accent"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Task
                          </Button>
                        )}
                      </div>

                      <p className="text-sm text-gray-500">
                        Created: {new Date(plan.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </CardContent>
                  </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Closed Plans */}
              <TabsContent value="closed" className="space-y-6">
                <div className="grid gap-6">
                  {actionPlans.filter(plan => plan.status === 'closed').length === 0 ? (
                    <Card className="glass border-gray-200">
                      <CardContent className="py-12 text-center text-gray-500">
                        No closed action plans yet. Complete all tasks in an action plan to close it.
                      </CardContent>
                    </Card>
                  ) : (
                    actionPlans.filter(plan => plan.status === 'closed').map((plan) => (
                      <Card key={plan.id} className="glass border-gray-200 shadow-lg bg-green-50/50" data-testid={`closed-plan-card-${plan.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="flex items-center space-x-2">
                                <CheckCircle2 className="w-6 h-6 text-green-600" />
                                <span>{plan.title}</span>
                                <Badge className="bg-green-100 text-green-800 border-green-200">Completed</Badge>
                              </CardTitle>
                              <CardDescription>{plan.description}</CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleReopenPlan(plan.id)}
                                variant="ghost"
                                size="sm"
                                className="text-brand-accent hover:text-brand-accent"
                                title="Reopen plan"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteActionPlan(plan.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                title="Delete plan"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">Progress</span>
                              <span className="text-sm font-semibold text-green-600">100%</span>
                            </div>
                            <Progress value={100} className="h-2 bg-green-100" />
                          </div>

                          <div className="space-y-2">
                            {plan.tasks.map((taskObj, idx) => (
                              <div
                                key={idx}
                                className="flex items-center space-x-3 p-3 bg-white/50 rounded-lg"
                              >
                                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                                <span className="line-through text-gray-500">{taskObj.task}</span>
                              </div>
                            ))}
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2 text-sm text-gray-500">
                            <div>
                              <span className="font-medium">Created:</span> {new Date(plan.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                            {plan.closed_at && (
                              <div>
                                <span className="font-medium">Completed:</span> {new Date(plan.closed_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* New Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {reviewTemplate?.period_type === 'weekly' ? 'Weekly' : 'Monthly'} Review
            </DialogTitle>
            <DialogDescription>
              Edit your review template and save it
            </DialogDescription>
          </DialogHeader>
          {reviewTemplate && (
            <div className="space-y-6 py-4">
              {/* Quick Stats */}
              {reviewTemplate.stats && (
                <div className="bg-brand-accent/5 p-4 rounded-lg border border-brand-border">
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <div className="text-2xl font-bold text-brand-accent">{reviewTemplate.stats.total || 0}</div>
                      <div className="text-xs text-gray-600">Reflections</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{reviewTemplate.stats.sentiments?.positive || 0}</div>
                      <div className="text-xs text-gray-600">Positive</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-600">{reviewTemplate.stats.sentiments?.neutral || 0}</div>
                      <div className="text-xs text-gray-600">Neutral</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">{reviewTemplate.stats.sentiments?.negative || 0}</div>
                      <div className="text-xs text-gray-600">Challenges</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Q&A Form */}
              <div className="space-y-4">
                {reviewTemplate.period_type === 'weekly' ? (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        1. What were your biggest wins this week?
                      </label>
                      <Textarea
                        value={reviewAnswers.wins || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, wins: e.target.value})}
                        placeholder="Write about your achievements and successes..."
                        className="min-h-[80px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        2. What challenges did you face and how did you handle them?
                      </label>
                      <Textarea
                        value={reviewAnswers.challenges || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, challenges: e.target.value})}
                        placeholder="Describe the difficult moments and your approach..."
                        className="min-h-[80px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        3. What patterns are you noticing in your thoughts and behaviors?
                      </label>
                      <Textarea
                        value={reviewAnswers.patterns || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, patterns: e.target.value})}
                        placeholder="Reflect on recurring themes..."
                        className="min-h-[80px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        4. What are you grateful for this week?
                      </label>
                      <Textarea
                        value={reviewAnswers.gratitude || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, gratitude: e.target.value})}
                        placeholder="List things you're thankful for..."
                        className="min-h-[60px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        5. What are your key action items for next week?
                      </label>
                      <Textarea
                        value={reviewAnswers.top3Actions || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, top3Actions: e.target.value})}
                        placeholder="List your action items (one per line, max 20)&#10;Example:&#10;Complete project proposal&#10;Exercise 3 times&#10;Call mom"
                        className="min-h-[100px]"
                        spellCheck
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 List 1-20 action items (one per line) - these can be converted to an action plan
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        1. What were your biggest achievements this month?
                      </label>
                      <Textarea
                        value={reviewAnswers.achievements || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, achievements: e.target.value})}
                        placeholder="Highlight your accomplishments..."
                        className="min-h-[80px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        2. What obstacles did you overcome?
                      </label>
                      <Textarea
                        value={reviewAnswers.obstacles || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, obstacles: e.target.value})}
                        placeholder="Challenges you faced and conquered..."
                        className="min-h-[80px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        3. What habits or patterns emerged?
                      </label>
                      <Textarea
                        value={reviewAnswers.habits || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, habits: e.target.value})}
                        placeholder="Behavioral patterns you noticed..."
                        className="min-h-[80px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        4. What are you most proud of this month?
                      </label>
                      <Textarea
                        value={reviewAnswers.proud || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, proud: e.target.value})}
                        placeholder="Moments of pride..."
                        className="min-h-[60px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        5. What lessons did you learn?
                      </label>
                      <Textarea
                        value={reviewAnswers.lessons || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, lessons: e.target.value})}
                        placeholder="Key takeaways..."
                        className="min-h-[60px]"
                        spellCheck
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        6. What are your key action items for next month?
                      </label>
                      <Textarea
                        value={reviewAnswers.top3Actions || ''}
                        onChange={(e) => setReviewAnswers({...reviewAnswers, top3Actions: e.target.value})}
                        placeholder="List your action items (one per line, max 20)&#10;Example:&#10;Launch new product feature&#10;Complete certification course&#10;Establish morning routine"
                        className="min-h-[100px]"
                        spellCheck
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 List 1-20 action items (one per line) - these can be converted to an action plan
                      </p>
                    </div>
                  </>
                )}
                
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-900">
                  <strong>Note:</strong> This review will be saved with all your answers. You can edit or delete it anytime from the Reviews tab.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setShowReviewDialog(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveReview}
              className="bg-brand-accent hover:bg-brand-accent text-white"
            >
              Save Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Action Plan from Review Dialog */}
      <Dialog open={showCreateActionPlanFromReview} onOpenChange={setShowCreateActionPlanFromReview}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Create Action Plan?</DialogTitle>
            <DialogDescription>
              We noticed you&apos;ve outlined some actions in your review. Would you like to turn them into an action plan?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm font-semibold text-gray-900 mb-2">Actions identified:</p>
            <ul className="space-y-2 mb-4">
              {reviewActionItems.slice(0, 5).map((item, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start">
                  <span className="text-brand-accent mr-2">•</span>
                  <span>{item.replace(/^[\-\*\•\d]+\.?\s*/, '').trim()}</span>
                </li>
              ))}
              {reviewActionItems.length > 5 && (
                <li className="text-sm text-gray-500 italic">
                  ...and {reviewActionItems.length - 5} more
                </li>
              )}
            </ul>
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-900">
              <strong>Tip:</strong> Creating an action plan helps you track progress on these items with task completion and progress monitoring.
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowCreateActionPlanFromReview(false);
                setReviewActionItems([]);
                setReviewPeriodType('');
              }}
              variant="outline"
            >
              No Thanks
            </Button>
            <Button
              onClick={handleCreateActionPlanFromReview}
              className="bg-brand-accent hover:bg-brand-accent text-white"
            >
              Create Action Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* What is a Reflective Practitioner Dialog */}
      <Dialog open={showReflectivePractitionerGuide} onOpenChange={setShowReflectivePractitionerGuide}>
        <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-brand-accent">
              What is a &quot;Reflective Practitioner&quot;?
            </DialogTitle>
            <DialogDescription>
              Understanding the foundations of reflective practice and personal growth
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {/* Definition */}
            <section>
              <div className="space-y-3 text-gray-700">
                <p className="leading-relaxed">
                  A reflective practitioner is someone who regularly evaluates their own experiences, actions, and decisions to learn and improve. This concept was popularized by <strong>Donald Schön</strong>, who emphasized the importance of professionals thinking about what they do both during (<em>reflection-in-action</em>) and after (<em>reflection-on-action</em>) their activities.
                </p>
                <div className="bg-brand-accent/5 p-4 rounded-lg border border-brand-border">
                  <p className="text-brand-ink font-medium">
                    🧠 <strong>In simple terms:</strong> It&apos;s the habit of asking &quot;What happened? Why did it happen that way? What can I learn? What will I do differently next time?&quot;
                  </p>
                </div>
              </div>
            </section>

            {/* How Reflection Builds Self-Awareness */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How Reflection Builds Self-Awareness
              </h3>
              <p className="text-gray-700 mb-3">Reflection helps build self-awareness by:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Encouraging deeper understanding of personal thoughts, feelings, and behaviours</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Identifying biases, triggers, or blind spots</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Clarifying values, beliefs, and goals</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Highlighting patterns in decision-making or emotional responses</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Leading to more intentional actions and better self-regulation</span>
                </li>
              </ul>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200 mt-3">
                <p className="text-green-900 text-sm">
                  <strong>Result:</strong> You begin to notice your strengths, weaknesses, and opportunities for growth more clearly. 🌱
                </p>
              </div>
            </section>

            {/* Connection to Growth Mindset */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Connection to Growth Mindset
              </h3>
              <p className="text-gray-700 mb-3">
                A <strong>growth mindset</strong>, coined by Carol Dweck, is the belief that abilities and intelligence can be developed through effort, learning, and persistence. Reflective practice complements growth mindset because:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Reflection helps recognize failure as a learning opportunity rather than a setback</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Encourages continuous improvement</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Reinforces the idea that change and development are possible</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">•</span>
                  <span>Helps people adjust strategies instead of giving up</span>
                </li>
              </ul>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-3">
                <p className="text-blue-900 text-sm">
                  <strong>In short:</strong> Reflection fuels the growth mindset by turning experiences into lessons.
                </p>
              </div>
            </section>

            {/* How the App Supports Reflective Practice */}
            <section className="bg-brand-bg p-5 rounded-lg border border-brand-border">
              <h3 className="text-xl font-bold text-brand-ink mb-3 flex items-center">
                📱 How does the ReflectiveMind Application support Reflective Practice?
              </h3>
              <p className="text-gray-700 mb-3">This application helps users:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">✓</span>
                  <span><strong>Capture reflections consistently</strong> through text or voice input</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">✓</span>
                  <span><strong>Organize them meaningfully</strong> with tags and sentiment analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">✓</span>
                  <span><strong>Summarize insights across time</strong> with AI-powered weekly and monthly reviews</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">✓</span>
                  <span><strong>Create action plans from those insights</strong> to turn learning into practice</span>
                </li>
                <li className="flex items-start">
                  <span className="text-brand-accent mr-2 mt-1">✓</span>
                  <span><strong>Track growth and goals</strong> with progress monitoring and emotional trend charts</span>
                </li>
              </ul>
            </section>

            {/* Closing Note */}
            <section className="border-t pt-4">
              <p className="text-gray-600 text-sm italic text-center">
                Remember: Becoming a reflective practitioner is a journey, not a destination. Each reflection brings you one step closer to greater self-awareness and intentional growth. 🌟
              </p>
            </section>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              onClick={() => setShowReflectivePractitionerGuide(false)}
              className="bg-brand-accent hover:bg-brand-accent-hover text-white"
            >
              Got it, thanks!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Guide Dialog */}
      <Dialog open={showUserGuide} onOpenChange={setShowUserGuide}>
        <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-brand-accent">
              ReflectiveMind User Guide
            </DialogTitle>
            <DialogDescription>
              Complete guide to using ReflectiveMind for personal growth and reflection
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-8">
            {user?.app_mode === 'lite' && (
              <>
                {/* Lite Guide — focused on the two tools available */}
                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">1</span>
                    Welcome
                  </h3>
                  <div className="ml-11 space-y-2 text-gray-700">
                    <p>
                      <strong>ReflectiveMind</strong> helps you turn small moments into steady growth. Your edition is the focused
                      reflective loop — two tabs, no noise.
                    </p>
                    <ul className="list-disc ml-5 space-y-1 text-sm">
                      <li><strong>Reflect</strong> — capture a thought (type or speak) and let us tag the theme and tone.</li>
                      <li><strong>Weekly Insight</strong> — when you&apos;re ready, generate a single AI summary of your past 7 days.</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">2</span>
                    Capturing a reflection
                  </h3>
                  <div className="ml-11 space-y-3 text-gray-700 text-sm">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Type or speak</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li><strong>Text:</strong> write what&apos;s on your mind in the box. Don&apos;t worry about polish.</li>
                        <li><strong>Voice:</strong> tap the microphone for up to <strong>60 seconds</strong>. We transcribe automatically.</li>
                        <li><strong>Prompts:</strong> tap <em>New Prompt</em> if you&apos;re unsure what to reflect on.</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Tags &amp; tone</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Add your own tags, or tap <em>Suggest Tags</em> for AI ideas.</li>
                        <li>Every reflection is auto-classified as Positive, Neutral, or Challenging — so you can see your emotional pattern at a glance on the chart.</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Edit &amp; search</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Use the pencil/trash icons on any reflection to edit or remove it.</li>
                        <li>Filter by tag or sentiment, or search the text to find a moment again.</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">3</span>
                    Your Weekly Insight
                  </h3>
                  <div className="ml-11 space-y-3 text-gray-700 text-sm">
                    <p>
                      Open the <strong>Weekly Insight</strong> tab and tap <em>Generate Insights</em> when you want a summary.
                      It&apos;s entirely on-demand — there&apos;s no auto-email.
                    </p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>Themes:</strong> the 3–5 patterns that came up most.</li>
                      <li><strong>Summary:</strong> a short narrative of your week.</li>
                      <li><strong>Emotional trend:</strong> how the week tilted, in numbers.</li>
                      <li><strong>Frequent words:</strong> the language you returned to.</li>
                    </ul>
                    <p>
                      Every insight you generate is saved here, so you can scroll back through past weeks and watch your
                      patterns evolve. Export any one to PDF with the download icon.
                    </p>
                    <div className="bg-brand-accent/5 border border-brand-border rounded-lg p-3">
                      <strong>One per week.</strong> You can generate a new insight at the start of each new week.
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                    <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">4</span>
                    Tips for a useful practice
                  </h3>
                  <div className="ml-11 text-gray-700 text-sm">
                    <ul className="list-disc ml-5 space-y-2">
                      <li><strong>5 minutes is enough.</strong> A small reflection most days beats a long one once a month.</li>
                      <li><strong>Be honest.</strong> Your reflections are private — you&apos;re writing for yourself.</li>
                      <li><strong>Tag consistently.</strong> Reuse the same labels (e.g. <em>work, family, mindset</em>) so patterns surface.</li>
                      <li><strong>Generate when it feels heavy.</strong> The weekly summary helps you step back and see the shape.</li>
                    </ul>
                  </div>
                </section>

                <section className="border-t pt-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Also available in the full version</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Your edition keeps things tight on purpose. The full ReflectiveMind app additionally includes:
                  </p>
                  <ul className="list-disc ml-5 text-sm text-gray-600 space-y-1">
                    <li>Monthly, quarterly and yearly AI summaries (meta-analysis of your insights).</li>
                    <li>Structured Weekly and Monthly Reviews with guided prompts.</li>
                    <li>Action Plans — convert insights into trackable tasks with progress.</li>
                    <li>Long-term analytics: trend evolution, growth tracking, seasonal patterns.</li>
                  </ul>
                </section>

                <section className="border-t pt-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Need help?</h3>
                  <p className="text-gray-700 text-sm">
                    If something feels off, reach out through your account settings.
                    Reflection is a practice — the more you use it, the more you&apos;ll see.
                  </p>
                </section>
              </>
            )}

            {user?.app_mode !== 'lite' && (
            <>
            {/* Getting Started */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">1</span>
                Getting Started
              </h3>
              <div className="ml-11 space-y-2 text-gray-700">
                <p><strong>Welcome to ReflectiveMind!</strong> This app helps you practice reflective thinking, track your personal growth, and turn insights into actionable goals.</p>
                <p>Navigate between tabs to access different features: <strong>Reflect</strong>, <strong>AI Insights</strong>, <strong>Personal Reviews</strong>, and <strong>Action Plans</strong>.</p>
              </div>
            </section>

            {/* Reflect Tab */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">2</span>
                Reflect Tab - Capturing Your Thoughts
              </h3>
              <div className="ml-11 space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">✍️ Create a Reflection</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Text Input:</strong> Type your thoughts in the text area</li>
                    <li><strong>Voice Input:</strong> Click the microphone icon to dictate your reflection (browser speech-to-text)</li>
                    <li><strong>Daily Prompts:</strong> Click &quot;Get Daily Prompt&quot; for inspiration</li>
                    <li><strong>Tags:</strong> Add custom tags or use &quot;Suggest Tags&quot; for AI recommendations</li>
                    <li><strong>Save:</strong> Click &quot;Save Reflection&quot; to store your entry</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📊 View & Analyze</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Sentiment Analysis:</strong> Each reflection is automatically analyzed (Positive/Neutral/Challenging)</li>
                    <li><strong>Search & Filter:</strong> Find reflections by text or filter by sentiment/tags</li>
                    <li><strong>Sentiment Trend Chart:</strong> Visual representation of your emotional patterns over 30 days</li>
                    <li><strong>Edit/Delete:</strong> Click the edit or delete icons on any reflection card</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">💾 Export Your Data</h4>
                  <p className="text-gray-700 text-sm">Click &quot;Export to Excel&quot; to download all your reflections with timestamps, sentiment analysis, and tags in a formatted spreadsheet.</p>
                </div>
              </div>
            </section>

            {/* AI Insights Tab */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">3</span>
                AI Insights - Discover Patterns
              </h3>
              <div className="ml-11 space-y-3">
                <div className="bg-brand-accent/5 border border-brand-border rounded-lg p-4 mb-3">
                  <h4 className="font-semibold text-brand-ink mb-2 flex items-center">
                    <Crown className="w-4 h-4 mr-2" />
                    Subscription Tiers
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="font-semibold text-gray-900">✅ Basic (Free):</p>
                      <ul className="ml-5 list-disc text-gray-700">
                        <li>Weekly insights</li>
                        <li>All reflection features</li>
                        <li>Sentiment tracking</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-brand-ink flex items-center">
                        <Crown className="w-3 h-3 mr-1" />
                        Premium:
                      </p>
                      <ul className="ml-5 list-disc text-gray-700">
                        <li><strong>{pricing?.monthly?.formatted || '£1.00'}/month</strong> or <strong className="text-brand-accent">{pricing?.annual?.formatted || '£10.00'}/year</strong> (save {pricing?.annual?.savings_percentage || 33}%)</li>
                        <li>Monthly, Quarterly & Yearly insights</li>
                        <li>Action plan management</li>
                        <li>Advanced analytics</li>
                      </ul>
                    </div>
                    <p className="text-brand-accent text-xs mt-2 font-semibold">
                      🎉 New users get 14 days of Premium FREE!
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📋 Four Time Periods</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Weekly:</strong> Analyzes your past 7 days of reflections (Basic & Premium)</li>
                    <li><strong>Monthly:</strong> Reviews your entire month of reflections <span className="text-brand-accent font-semibold">(Premium)</span></li>
                    <li><strong>Quarterly:</strong> Meta-analysis of 3 monthly insights <span className="text-brand-accent font-semibold">(Premium)</span></li>
                    <li><strong>Yearly:</strong> Meta-analysis of 4 quarterly or 12 monthly insights <span className="text-brand-accent font-semibold">(Premium)</span></li>
                    <li><strong>Organized Tabs:</strong> Each period has its own tab for easy navigation</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🤖 How Insights Work</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Weekly & Monthly:</strong> AI analyzes your raw reflections</li>
                    <li><strong>Quarterly & Yearly:</strong> AI analyzes your previous insights (meta-analysis)</li>
                    <li><strong>One Per Period:</strong> Generate once per period to prevent duplicates</li>
                    <li><strong>Auto-Analysis:</strong> Identifies themes, patterns, sentiment trends, and frequent words</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📊 Enhanced Analytics (Quarterly & Yearly)</h4>
                  <p className="text-gray-700 text-sm mb-2">Longer-period insights include powerful additional features:</p>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Trend Analysis:</strong> How themes evolved over time (emerged, persisted, or faded)</li>
                    <li><strong>Growth Tracking:</strong> Action plan completion rates and progress metrics</li>
                    <li><strong>Pattern Comparison:</strong> Persistent vs changing patterns</li>
                    <li><strong>Sentiment Evolution:</strong> Emotional journey tracking period-by-period</li>
                    <li><strong>Peak Insights:</strong> Your most significant breakthroughs and realizations</li>
                    <li><strong>Seasonal Patterns (Yearly):</strong> Recurring patterns in specific months/quarters</li>
                  </ul>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="font-semibold text-brand-ink mb-1">⚠️ Important: Keep Your Insights</h4>
                  <ul className="list-disc ml-5 space-y-1 text-brand-cta text-sm">
                    <li><strong>For Quarterly:</strong> Need to keep 3 monthly insights</li>
                    <li><strong>For Yearly:</strong> Need to keep 4 quarterly OR 12 monthly insights</li>
                    <li><strong>Why?</strong> Quarterly/yearly insights are built from previous insights (meta-analysis)</li>
                    <li><strong>Export First:</strong> Download insights as PDF before deleting if needed</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">💡 Suggested Action Plans</h4>
                  <p className="text-gray-700 text-sm">Each insight includes an AI-suggested action plan with specific tasks. Click <strong>&quot;Create Action Plan&quot;</strong> to add it directly to your Action Plans tab. Quarterly and yearly plans are more strategic and long-term.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📄 Export & Delete</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Export to PDF:</strong> Download insights for offline review or to share with mentors/coaches</li>
                    <li><strong>Delete with Care:</strong> When deleting monthly/quarterly insights, you&apos;ll see a warning about longer-period requirements</li>
                    <li><strong>Best Practice:</strong> Export before deleting to keep records</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🎯 Recommended Usage</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Weekly:</strong> Quick check-ins, identify immediate patterns</li>
                    <li><strong>Monthly:</strong> Deeper reflection, track monthly themes</li>
                    <li><strong>Quarterly:</strong> Strategic planning, see 3-month evolution</li>
                    <li><strong>Yearly:</strong> Annual review, celebrate growth, plan ahead</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Personal Reviews Tab */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">4</span>
                Personal Reviews - Structured Reflection
              </h3>
              <div className="ml-11 space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📅 Weekly Reviews</h4>
                  <p className="text-gray-700 text-sm mb-2">Complete a structured review every week with 5 guided questions:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-gray-700 text-sm">
                    <li>What were my biggest wins this week?</li>
                    <li>What challenges did I face and how did I handle them?</li>
                    <li>What patterns am I noticing in my thoughts and behaviors?</li>
                    <li>What am I grateful for this week?</li>
                    <li>What are your key action items for next week? (1-20 items)</li>
                  </ol>
                  <p className="text-gray-700 text-sm mt-2"><strong>Export:</strong> Download as PDF | <strong>One per week:</strong> Create new review every Monday</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📆 Monthly Reviews</h4>
                  <p className="text-gray-700 text-sm mb-2">Deeper monthly reflection with 6 comprehensive questions:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-gray-700 text-sm">
                    <li>What were my biggest achievements this month?</li>
                    <li>What obstacles did I overcome?</li>
                    <li>What habits or patterns emerged?</li>
                    <li>What am I most proud of this month?</li>
                    <li>What lessons did I learn?</li>
                    <li>What are your key action items for next month? (1-20 items)</li>
                  </ol>
                  <p className="text-gray-700 text-sm mt-2"><strong>Export:</strong> Download as PDF | <strong>One per month:</strong> Create new review every 1st of the month</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🎯 Turn Actions into Plans</h4>
                  <p className="text-gray-700 text-sm">When you list your action items (1-20 items) in a review, the app will prompt you to convert them into an Action Plan automatically. This creates trackable tasks with progress monitoring.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📈 Emotional Trends</h4>
                  <p className="text-gray-700 text-sm">View your emotional patterns over time with visual charts. Switch between weekly and monthly views to see how your sentiment changes across periods.</p>
                </div>
              </div>
            </section>

            {/* Action Plans Tab */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">5</span>
                Action Plans - Turn Insights into Goals
                <span className="ml-3 text-sm font-normal px-3 py-1 bg-brand-accent/10 text-brand-accent rounded-full flex items-center">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium Feature
                </span>
              </h3>
              <div className="ml-11 space-y-3">
                <div className="bg-brand-accent/5 border border-brand-border rounded-lg p-3 mb-3">
                  <p className="text-sm text-brand-ink">
                    <strong>Premium Feature:</strong> Action Plans require a Premium subscription ({pricing?.monthly?.formatted || '£1.00'}/month or {pricing?.annual?.formatted || '£10.00'}/year - save {pricing?.annual?.savings_percentage || 33}%). Basic users can access all other features including weekly insights and reflections.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📊 Statistics Dashboard</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Plans Overview:</strong> See your Active, Closed, and Total plans at a glance</li>
                    <li><strong>Tasks Overview:</strong> Track Completed, Incomplete, and Total tasks across all plans</li>
                    <li><strong>Visual Progress:</strong> Color-coded cards help you monitor your productivity</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">➕ Create Action Plans</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Manual Creation:</strong> Click &quot;Create Plan&quot; and fill in title, description, and tasks</li>
                    <li><strong>From AI Insights:</strong> Click &quot;Create Action Plan&quot; on any insight card</li>
                    <li><strong>From Reviews:</strong> Automatically prompted when you add action items (1-20 items) in reviews</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">📋 Active & Closed Plans</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Active Plans Tab:</strong> Shows plans you&apos;re currently working on</li>
                    <li><strong>Closed Plans Tab:</strong> Completed plans with 100% progress</li>
                    <li><strong>Auto-Close:</strong> When you complete the last task, the plan automatically moves to Closed</li>
                    <li><strong>Reopen Plans:</strong> Click the reopen icon (↻) on any closed plan to move it back to Active</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">🔄 Sort & Organize</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Sort Options:</strong> View plans by &quot;Newest First&quot; or &quot;Oldest First&quot;</li>
                    <li><strong>Same Sorting:</strong> Sort order applies to both Active and Closed plans</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">✏️ Edit & Track Progress</h4>
                  
                  <p className="text-gray-700 text-sm mb-2"><strong>Two Types of Plans:</strong></p>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm mb-3">
                    <li><strong>Active Plans (Never Closed):</strong> Fully editable with Edit button visible - you can modify everything</li>
                    <li><strong>Reopened Plans (Previously Completed):</strong> Show amber &quot;Reopened • Read-only&quot; badge - can only toggle task completion, cannot edit text or add/delete tasks</li>
                  </ul>
                  
                  <p className="text-gray-700 text-sm mb-2"><strong>Editing Active Plans:</strong></p>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm mb-3">
                    <li><strong>Enter Edit Mode:</strong> Click the edit icon (pencil) - a blue banner appears showing you&apos;re in edit mode</li>
                    <li><strong>Edit Title & Description:</strong> Modify plan details in the text fields at the top</li>
                    <li><strong>Edit Task Text:</strong> Each task becomes an editable input field - click to modify descriptions</li>
                    <li><strong>Add Tasks:</strong> Click &quot;Add Task&quot; button (dashed accent border) at the bottom to create new tasks</li>
                    <li><strong>Delete Tasks:</strong> Click the trash icon next to any task to remove it instantly</li>
                    <li><strong>Toggle Completion:</strong> Click checkboxes to mark tasks complete/incomplete while editing</li>
                    <li><strong>Save Changes:</strong> Click green Save button to apply all changes, or Cancel to discard everything</li>
                  </ul>
                  
                  <p className="text-gray-700 text-sm mb-2"><strong>Tracking Progress:</strong></p>
                  <ul className="list-disc ml-5 space-y-1 text-gray-700 text-sm">
                    <li><strong>Toggle Tasks:</strong> Click checkbox icons to mark tasks complete/incomplete (works in view mode)</li>
                    <li><strong>Visual Feedback:</strong> Completed tasks show checkmark and strikethrough text</li>
                    <li><strong>Progress Bar:</strong> Updates automatically as you complete tasks</li>
                    <li><strong>Auto-Close:</strong> Completing the last task automatically moves plan to Closed with celebration toast</li>
                    <li><strong>Delete Plans:</strong> Remove plans you no longer need from either Active or Closed tabs</li>
                    <li><strong>View History:</strong> See creation date on all plans, plus completion date on closed plans</li>
                  </ul>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <h5 className="font-semibold text-blue-900 mb-1 text-sm">💡 Visual Indicators</h5>
                    <ul className="list-disc ml-5 space-y-1 text-blue-800 text-xs">
                      <li><strong>Edit Mode Banner:</strong> &quot;Edit Mode&quot; indicator shows when you&apos;re editing a plan</li>
                      <li><strong>Amber Badge:</strong> &quot;Reopened • Read-only&quot; badge shows plans that were completed and reopened</li>
                      <li><strong>Highlighted Background:</strong> Tasks in edit mode have a soft accent background for clarity</li>
                      <li><strong>Brand Checkmarks:</strong> Completed tasks show a blue checkmark and a soft accent background</li>
                      <li><strong>Progress Colors:</strong> Visual progress tracking with color-coded indicators</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Best Practices */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">6</span>
                Best Practices & Tips
              </h3>
              <div className="ml-11 space-y-2 text-gray-700">
                <ul className="list-disc ml-5 space-y-2 text-sm">
                  <li><strong>Daily Habit:</strong> Reflect for 5-10 minutes each day for best results</li>
                  <li><strong>Be Honest:</strong> Your reflections are private - write authentically</li>
                  <li><strong>Use Tags:</strong> Consistent tagging helps you find patterns later</li>
                  <li><strong>Weekly Reviews:</strong> Complete every Friday or Sunday to close out your week</li>
                  <li><strong>Generate Insights Regularly:</strong> Weekly insights weekly, monthly insights monthly - consistency reveals patterns</li>
                  <li><strong>Keep Monthly Insights:</strong> Save them for at least 3 months to enable quarterly insights</li>
                  <li><strong>Build to Yearly:</strong> Keep quarterly insights for annual reviews and long-term growth tracking</li>
                  <li><strong>Action Plans:</strong> Keep tasks specific and achievable (SMART goals)</li>
                  <li><strong>Track Completion:</strong> Check off tasks as you complete them - plans automatically close when finished</li>
                  <li><strong>Review Statistics:</strong> Check your stats dashboard to monitor overall progress</li>
                  <li><strong>Review Closed Plans:</strong> Revisit completed plans for motivation and to see how far you&apos;ve come</li>
                  <li><strong>Export Before Delete:</strong> Download insights as PDF before deleting them</li>
                  <li><strong>Export Data:</strong> Regularly export your reflections as backup</li>
                  <li><strong>Voice Recording:</strong> Use when you&apos;re on the go or prefer speaking</li>
                </ul>
              </div>
            </section>

            {/* Keyboard Shortcuts & Quick Tips */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="bg-brand-accent/10 text-brand-accent rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">7</span>
                Quick Tips
              </h3>
              <div className="ml-11 space-y-2">
                <div className="bg-brand-accent/5 p-3 rounded-lg border border-brand-border">
                  <p className="text-sm text-brand-ink"><strong>💡 Tip:</strong> Start with daily prompts if you&apos;re unsure what to reflect on.</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900"><strong>💡 Tip:</strong> Review your emotional trends monthly to identify stress patterns.</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm text-green-900"><strong>💡 Tip:</strong> Link action plans back to your insights for motivation.</p>
                </div>
                <div className="bg-brand-accent-soft p-3 rounded-lg border border-brand-border">
                  <p className="text-sm text-brand-ink"><strong>💡 Tip:</strong> When you complete all tasks in a plan, it automatically moves to Closed Plans. You can always reopen it later!</p>
                </div>
                <div className="bg-brand-accent/5 p-3 rounded-lg border border-brand-border">
                  <p className="text-sm text-brand-ink"><strong>💡 Tip:</strong> Generate quarterly insights after 3 months to see how your themes evolved. Yearly insights reveal powerful long-term patterns!</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-sm text-brand-ink"><strong>💡 Tip:</strong> Keep your monthly insights for at least 3 months - they&apos;re needed to unlock quarterly and yearly meta-analysis!</p>
                </div>
              </div>
            </section>

            {/* Support */}
            <section className="border-t pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-gray-700 text-sm">If you encounter any issues or have questions, please reach out through your account settings or contact support.</p>
              <p className="text-gray-600 text-sm mt-2 italic">Remember: Reflection is a practice. The more you use ReflectiveMind, the more insights you&apos;ll gain about yourself!</p>
            </section>
            </>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              onClick={() => setShowUserGuide(false)}
              className="bg-brand-accent hover:bg-brand-accent text-white"
            >
              Got it, let&apos;s reflect!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        subscription={subscription}
      />

      {/* Legal consent gate — shown once until user accepts */}
      <ConsentModal />

      {/* Footer */}
      <footer className="container mx-auto px-3 sm:px-6 py-6 text-center text-gray-500">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-5 mb-2 text-xs">
          <Link
            to="/privacy"
            className="hover:text-brand-accent underline-offset-2 hover:underline"
            data-testid="dashboard-footer-privacy"
          >
            Privacy Policy
          </Link>
          <span className="hidden sm:inline text-gray-300">•</span>
          <Link
            to="/terms"
            className="hover:text-brand-accent underline-offset-2 hover:underline"
            data-testid="dashboard-footer-terms"
          >
            Terms of Service
          </Link>
        </div>
        <p className="text-xs" data-testid="dashboard-footer-copyright">
          &copy; {site?.copyright_year || new Date().getFullYear()}{' '}
          {site?.company_name || 'ReflectiveMind'}.
        </p>
      </footer>
    </div>
  );
};


export default Dashboard;
